/**
 * MALLO TOWELS — GLOBAL LOCALISATION + PHILIPPINES + JAPAN + MEXICO + UNITED KINGDOM
 * Version 6.0 • 2026-10-09 • Shopify Horizon
 *
 * Upload under Shopify / Online Store / Themes / Edit code / Assets.
 * Load once from theme.liquid using the snippet in the companion guide.
 *
 * Uses Shopify's *selected country* (PH / JP / MX / GB), not IP geolocation.
 * DOES NOT set currencies, convert prices, modify cart/product data,
 * calculate shipping, change checkout, or activate discounts.
 *
 * Includes existing AU/CA homepage spelling rules from v1.
 *
 * PREVIEW (only with debug: true in theme.liquid):
 *   ?mallo_preview=PH or ?mallo_preview=JP&mallo_lang=ja or ?mallo_preview=MX&mallo_lang=es or ?mallo_preview=GB
 * This only previews text and banners; it DOES NOT preview Philippine
 * prices, shipping, inventory, or checkout eligibility.
 */
(function () {
  'use strict';
  if (window.__MALLO_LOCALISATION_V6__) return;
  window.__MALLO_LOCALISATION_V6__ = true;

  /* ======================= EDITABLE CONFIG ======================= */
  const SETTINGS = {
    debug: false,                   // May also be enabled by Liquid context
    philippines: {
      enabled: true,
      banner: true,                 // Localise existing seasonal headline
      neutraliseFreeShippingClaim: true,
      pagePanels: true,             // Add locally relevant editorial panels
      productBenefits: true,
      faqAdditions: true,
      // Confirmed active for customers checking out with Philippines selected.
      promotion: {
        enabled: true,
        headline: 'PHILIPPINES LAUNCH • 25% OFF',
        code: 'AUTUMN25'
      }
    }
  };

  /* Shopify-selected market: supply context in theme.liquid (guide). */
  const context = window.MALLO_LOCALIZATION_CONTEXT || {};
  const rawCountry = String(context.country || (window.Shopify && window.Shopify.country) || '').toUpperCase();
  const language = String(context.language || (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en');
  const DEBUG = !!(context.debug || SETTINGS.debug);
  const previewMatch = DEBUG && /(?:^|[?&])mallo_preview=(PH|JP|MX|GB)(?:&|$)/i.exec(location.search);
  const preview = !!previewMatch;
  const country = preview ? previewMatch[1].toUpperCase() : rawCountry;
  const pageKey = String(context.pageKey || guessPageKey() || '');
  const rootPath = (() => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root.startsWith('/') ? root.replace(/\/+$/, '') + '/' : '/';
  })();
  const href = (path) => rootPath + String(path || '').replace(/^\/+/, '');
  const PH = country === 'PH' && SETTINGS.philippines.enabled;
  const JP = country === 'JP';
  const MX = country === 'MX';
  const UK = country === 'GB';
  const EXCLUDED = 'script,style,noscript,textarea,select,option,code,pre,svg,template,' +
    '[contenteditable], [data-mallo-no-localize], [data-mallo-ph], [data-mallo-jp], [data-mallo-mx], [data-mallo-uk], [data-mallo-uk], ' +
    '.money, .price, [data-price], [data-product-price], .shopify-payment-button,' +
    '.shopify-payment-button__button, [data-testid="price"]';

  function guessPageKey() {
    const path = decodeURIComponent(location.pathname).split('/').filter(Boolean);
    for (const type of ['pages', 'products', 'collections', 'policies', 'blogs']) {
      const index = path.indexOf(type);
      if (index >= 0 && path[index + 1]) return type + '/' + path.slice(index + 1).join('/');
    }
    if (path.includes('cart')) return 'cart';
    if (!path.length || path.every(p => /^[a-z]{2}(?:-[a-z]{2})?$/i.test(p))) return 'home';
    return '';
  }
  function log() { if (DEBUG) console.info('[Mallo localisation]', ...arguments); }
  function mainElement() { return document.querySelector('main#MainContent, main[role="main"], main'); }
  function normalized(value) { return String(value || '').replace(/\s+/g, ' ').trim(); }

  /* Precise original-copy replacements based on the live store audit.
     Limit all of these to the page's main content; never touch product
     names, variants, star ratings or dynamic price fields. */
  const COPY = {
    home: [
      ['Introducing the new', 'HELLO, PHILIPPINES!'],
      ['Turn every shower into a spa-like escape with plush texture and lasting softness.',
        'Make every shower feel like a little escape, with cloud-soft texture, beautiful colours and lightweight everyday comfort.'],
      ['Five new colorways. While stocks last.', 'Limited Colours. Unlimited Softness.'],
      ["The same cloud-soft Marshmallow ridge, in a run of colors that won't stick around — grab your favorite before it's gone.",
        'Your favourite Marshmallow softness in fresh, beautiful colours. Find the shade that makes your bathroom feel like you.']
    ],
    'pages/the-marshmallow-towel': [
      ['THICKER, SOFTER, AND FLUFFIER THAN EVER', 'CLOUD-SOFT COMFORT, NOW IN THE PHILIPPINES'],
      ['the ordinary shower towel reimagined', 'your everyday shower, a little softer'],
      ['REDEFINE YOUR SHOWER EXPERIENCE', 'MEET YOUR NEW FAVOURITE TOWELS'],
      ['Experience the ultimate comfort and cuteness with our Marshmallow Towel collection. Crafted with plush materials and adorned with pastel colors, these towels are designed to make every post-shower a delightful experience.',
        'Meet your next favourite bathroom upgrade. Our Marshmallow Towels bring velvety-soft ridges, beautiful colours and a light, comfortable feel to every post-shower moment.']
    ],
    'pages/pure-cotton-collection': [
      ['The luxury of 100% cotton, reimagined with a softer handfeel, plush texture, and elevated finish.',
        'Love the classic feel of cotton? Discover soft, beautifully textured 100% long-staple cotton for a hotel-inspired moment at home.'],
      ['Style Your Space', 'Bring Hotel-Inspired Comfort Home'],
      ['The perfect weight for everyday use.', 'A substantial, plush feel at 430 GSM.']
    ],
    'pages/the-chevron-towel': [
      ['DISCOVER CHEVRON', 'A LITTLE MORE STYLE IN EVERY SHOWER'],
      ['The Chevron Towel does it all.', 'Beautiful texture. Everyday comfort.'],
      ['Transform your daily routine with the iconic Chevron Towel. Combining style and sophistication, these towels boast chic pastel hues that elevate any bathroom decor. Crafted from premium materials, the Chevron Towel is highly absorbent and luxuriously soft, ensuring a spa-like experience every time you step out of the shower. Indulge in the unparalleled quality and comfort that makes the Chevron Towel a must-have addition to your home.',
        'Give your bathroom a little more personality. With its sculpted chevron pattern, lovely soft feel and distinctive shades, Chevron is a practical upgrade that looks as good as it feels.']
    ],
    'collections/all-1': [
      ['All Products', 'Explore Mallo Philippines'],
      ['Up to 40% Off Sitewide', 'Your next favourite towels start here.'],
      ['The Marshmallow Series you love, newly improved for even more softness and plush comfort.',
        'Meet our signature cloud-soft ridges: lightweight, beautifully textured and made for the routines you love.'],
      ['Limited-edition Marshmallow Towel releases, made in special colors for a short time only.',
        'Your favourite Marshmallow softness in special colours worth showing off. Explore the limited releases.']
    ],
    'collections/all': [
      ['All Products', 'Explore Mallo Philippines'],
      ['Up to 40% Off Sitewide', 'Your next favourite towels start here.']
    ],
    'pages/about-us': [
      ['Towels that feel good, every day.', 'Making Everyday Moments Feel Softer.']
    ],
    'pages/contact': [
      ["Whether you have a question about our products, need assistance with an order, or just want to say hello, we're here and ready to help. Your satisfaction is our top priority, and we're dedicated to providing you with exceptional customer service.",
        'Hello, Philippines! Have a question about towels, delivery options, or your order? Our Mallo team is happy to help.'],
      ['Thank you for choosing Mallo Towels.', 'Thanks for bringing a little Mallo softness into your day.']
    ],
    'pages/partners': [
      ['Softness, made for sharing.', 'A Softer Way to Gift, Host & Celebrate.'],
      ["Let's make something soft together.", 'Let’s make something memorable together.'],
      ['Gifts they’ll actually use.', 'Gifts they’ll enjoy using, every day.']
    ],
    'blogs/mallo-living': [
      ['Thoughtful guides on towels, fabric care, bathroom rituals, softness, wellness, and the small details that make everyday living feel more comfortable.',
        'Thoughtful guides to softer routines, easy towel care, beautiful bathrooms and everyday comfort — including tips for warm, humid days.']
    ]
  };

  // Retained from v1: harmless English spelling updates for AU and CA.
  const AU_CA_HOME = [
    ['Five new colorways. While stocks last.', 'Five new colourways. While stocks last.'],
    ["in a run of colors that won't stick around", "in a run of colours that won't stick around"],
    ["grab your favorite before it's gone", "grab your favourite before it's gone"]
  ];

  const PH_PANELS = {
    home: {
      eyebrow: 'PHILIPPINES, MEET MARSHMALLOW',
      title: 'The cutest towels you’ll ever own.',
      body: 'Picture the softest part of your day: fresh from the shower, wrapped in sculpted Marshmallow ridges. Beautiful enough to show off, light enough for everyday use.',
      points: ['Cloud-soft feel', 'Quick-drying design', 'Beautiful bathroom colours'],
      cta: 'Explore Signature Plush', url: 'pages/the-marshmallow-towel'
    },
    'pages/the-marshmallow-towel': {
      eyebrow: 'THE PHILIPPINES EDIT',
      title: 'A little more softness for humid days.',
      body: 'Signature Plush feels wonderfully soft and plush without the bulky feel of a traditional towel. Our signature ridges are designed to absorb moisture quickly and dry with everyday ease.',
      points: ['Plush, velvety texture', 'Lightweight handling', 'Everyday easy care'],
      cta: 'Shop all Mallo towels', url: 'collections/all-1'
    },
    'pages/pure-cotton-collection': {
      eyebrow: 'YOUR HOTEL-AT-HOME MOMENT',
      title: 'Prefer pure cotton? This one’s for you.',
      body: 'The 100% long-staple cotton Marshmallow Collection uses our CloudLoom™ construction at 430 GSM for an indulgently full, soft feel.',
      points: ['100% cotton', '430 GSM', 'Plush, hotel-inspired texture']
    },
    'pages/the-chevron-towel': {
      eyebrow: 'TEXTURE MEETS STYLE',
      title: 'Your bathroom deserves a little more personality.',
      body: 'Discover a sculpted chevron pattern and soft, comfortable feel that brings a thoughtful finishing touch to your everyday bathroom routine.',
      points: ['Distinctive chevron design', 'Soft everyday comfort', 'Thoughtfully chosen colours']
    },
    'collections/all-1': {
      eyebrow: 'WELCOME, PHILIPPINES',
      title: 'Find your kind of soft.',
      body: 'Start with our signature lightweight Marshmallow towels, choose the rich feel of Pure Cotton, or dress up your bathroom with Chevron. Matching packs are an easy way to share the softness.',
      points: ['Signature Plush', 'Pure Cotton', 'Chevron & limited colours']
    },
    'collections/all': {
      eyebrow: 'WELCOME, PHILIPPINES',
      title: 'Find your kind of soft.',
      body: 'Cloud-soft towels, beautiful colours and thoughtful textures for your everyday routine.'
    },
    'pages/about-us': {
      eyebrow: 'HELLO FROM MALLO',
      title: 'Bringing a little Marshmallow softness to the Philippines.',
      body: 'We believe the little things matter: a towel that feels lovely, looks beautiful and makes your regular routine feel special. Welcome to the Mallo community.'
    },
    'pages/faqs': {
      eyebrow: 'SHOPPING FROM THE PHILIPPINES?',
      title: 'A few things to know before you order.',
      body: 'Shipping availability, delivery charges and payment methods depend on your address and the options offered at checkout. Find more answers below or ask our support team.'
    },
    'pages/contact': {
      eyebrow: 'QUESTIONS FROM THE PHILIPPINES?',
      title: 'We’re happy to help.',
      body: 'Ask us about your order, towel materials, care, shipping details or returns. Please include your delivery city or province when asking about shipping.',
      cta: 'Email Mallo Support', externalUrl: 'mailto:support@mallotowels.com'
    },
    'pages/partners': {
      eyebrow: 'PHILIPPINES PARTNERSHIPS',
      title: 'Make every guest feel a little more special.',
      body: 'Planning corporate gifts, a hotel or resort experience, a spa, a wedding or a retail collaboration in the Philippines? Tell us about your project and the quantities you have in mind.',
      points: ['Corporate & employee gifts', 'Hotels, resorts & spas', 'Weddings & lifestyle retail']
    },
    'collections/limited-edition-releases': {
      eyebrow: 'LIMITED COLOURS',
      title: 'Limited colours. Unlimited softness.',
      body: 'Meet your favourite Marshmallow feel in beautiful special-release shades. Check current availability before your perfect colour disappears.'
    },
    'collections/bath-essentials': {
      eyebrow: 'A LITTLE EXTRA SELF-CARE',
      title: 'The finishing touches to your bathroom routine.',
      body: 'Discover Mallo’s little everyday indulgences. Product availability and eligible shipping destinations are determined at checkout.'
    },
    'blogs/mallo-living': {
      eyebrow: 'SOFT LIVING, ANY SEASON',
      title: 'Small changes, softer everyday moments.',
      body: 'Explore simple towel-care tips, calming bathroom ideas and routines that make everyday comfort feel effortless.'
    },
    'policies/shipping-policy': {
      eyebrow: 'PHILIPPINES SHIPPING INFORMATION',
      title: 'Check delivery options for your address.',
      body: 'For Philippine orders, the available delivery services and shipping charges are determined by your checkout address. Delivery times and any customs-related costs depend on the carrier, order and destination. If you need confirmation before ordering, please contact us.',
      cta: 'Ask about delivery', url: 'pages/contact', simple: true
    },
    'policies/refund-policy': {
      eyebrow: 'RETURNS FROM THE PHILIPPINES',
      title: 'Please contact us before sending anything back.',
      body: 'International return shipping may be required. The eligibility conditions and exclusions in the Returns Policy below still apply, including its conditions for discounted items. For damaged or incorrect items, contact support with your order details.',
      cta: 'Contact Mallo Support', url: 'pages/contact', simple: true
    },
    cart: {
      eyebrow: 'CHECKOUT FROM THE PHILIPPINES',
      title: 'Your delivery options appear at checkout.',
      body: 'Your order currency is handled by the store automatically. Delivery charges, available payment methods and any applicable order conditions are determined at checkout.',
      simple: true
    }
  };

  /* ================= JAPAN LANGUAGE / LOCALISATION =================
   * Selected market JP -> Japanese by default, optional English toggle.
   * Never manipulates currencies, tax, shipping prices, checkout, discounts,
   * variant choices, or form submission. This is a *client-side* editorial
   * localisation, not a complete Shopify language translation for SEO.
   */
  const JP_STORAGE_KEY = 'mallo_japan_language_v4';
  let japanLanguage = 'ja';
  const JP_ORIGINALS = new WeakMap();

  function savedJapanLanguage() {
    try { return localStorage.getItem(JP_STORAGE_KEY); } catch (_) { return null; }
  }
  function persistJapanLanguage(value) {
    try { localStorage.setItem(JP_STORAGE_KEY, value); } catch (_) { /* storage can be blocked */ }
  }
  function initializeJapanLanguage() {
    const params = new URLSearchParams(location.search);
    const requested = params.get('mallo_lang');
    if (requested === 'ja' || requested === 'en') return requested;
    const saved = savedJapanLanguage();
    return saved === 'ja' || saved === 'en' ? saved : 'ja';
  }

  const JP_BANNER = {
    ja: {
      'AUTUMN IS HERE 🍂': '日本の皆さま、こんにちは 🇯🇵',
      'Enjoy 25% OFF with code AUTUMN25': 'クーポン AUTUMN25 で25%OFF',
      'Free tracked shipping over $99': '配送料はチェックアウト時にご確認ください',
      'Free shipping on orders over $99.': '送料はチェックアウト時にご確認ください。',
      'Free Shipping on qualifying orders': '送料はチェックアウト時にご確認ください',
      'Free Shipping On qualifying orders': '送料はチェックアウト時にご確認ください'
    },
    en: {
      'AUTUMN IS HERE 🍂': 'HELLO, JAPAN 🇯🇵',
      'Enjoy 25% OFF with code AUTUMN25': '25% OFF with code AUTUMN25',
      'Free tracked shipping over $99': 'Shipping options shown at checkout',
      'Free shipping on orders over $99.': 'Shipping options are shown at checkout.',
      'Free Shipping on qualifying orders': 'Shipping shown at checkout',
      'Free Shipping On qualifying orders': 'Shipping shown at checkout'
    }
  };

  /* Match full visible text nodes so we do not replace prices, SKUs,
     handles, review text, or arbitrary fragments of other words. */
  const JP_COMMON = {
    'Shop': 'ショップ',
    'Shop All': 'すべての商品',
    'SHOP ALL': 'すべての商品を見る',
    'Shop Now': '商品を見る',
    'SHOP NOW': '商品を見る',
    'Learn More': '詳しく見る',
    'Read our story': 'ブランドストーリーを見る',
    'About': 'Malloについて',
    'About Us': 'Malloについて',
    'Our Story': '私たちのストーリー',
    'Signature Plush': 'シグネチャープラッシュ',
    'SIGNATURE PLUSH': 'シグネチャープラッシュ',
    'Pure Cotton': 'ピュアコットン',
    'PURE COTTON': 'ピュアコットン',
    'The Chevron Series': 'シェブロンシリーズ',
    'Chevron Series': 'シェブロンシリーズ',
    'Limited Edition': '限定カラー',
    'LIMITED EDITION': '限定カラー',
    'Bath Essentials': 'バスアイテム',
    'Choose Options': 'カラー・種類を選ぶ',
    'Choose options': 'カラー・種類を選ぶ',
    'Add to Cart': 'カートに追加',
    'Add to cart': 'カートに追加',
    'View cart': 'カートを見る',
    'View Cart': 'カートを見る',
    'View Product': '商品を見る',
    'Checkout': '購入手続きへ',
    'Check out': '購入手続きへ',
    'Continue shopping': '買い物を続ける',
    'Search': '検索',
    'Log in': 'ログイン',
    'Best Sellers': '人気商品',
    'BESTSELLERS': '人気商品',
    'SHOP OUR BESTSELLERS': '人気商品を見る',
    'Material': '素材',
    'Feel': '肌ざわり',
    'Absorbency': '吸水性',
    'Best for': 'こんな方におすすめ',
    'Material:': '素材：',
    'Fabric:': '生地：',
    'Color': 'カラー',
    'Colour': 'カラー',
    'Size': 'サイズ',
    'Quantity': '数量',
    'Reviews': 'レビュー',
    'Verified Buyer': '購入者レビュー',
    'VERIFIED BUYER': '購入者レビュー',
    'Verified': '購入者レビュー',
    'Free Shipping': '送料無料',
    'Secure Checkout': '安全なお支払い',
    'Easy Returns': '返品について',
    'Contact': 'お問い合わせ',
    'Contact Us': 'お問い合わせ',
    'FAQs': 'よくあるご質問',
    'Shipping Policy': '配送について',
    'Refund Policy': '返品・返金ポリシー',
    'Returns Policy': '返品ポリシー',
    'Read the journal': '記事を読む',
    'From the journal': 'Malloジャーナル',
    'Our Philosophy': 'Malloの想い',
    'Why Mallo': 'Malloが選ばれる理由',
    'why mallo': 'Malloが選ばれる理由',
    'Shop Signature Plush': 'シグネチャープラッシュを見る',
    'Shop Pure Cotton': 'ピュアコットンを見る',
    'Shop Bath Essentials': 'バスアイテムを見る',
    'Shop Mallo': 'Malloの商品を見る',
    'Shop Mallo Towels': 'タオルを見る',
    'Marshmallow Family Pack': 'マシュマロ ファミリーパック',
    'Marshmallow Partner Pack': 'マシュマロ ペアパック',
    'Marshmallow Towel Pack': 'マシュマロ タオルセット',
    'Marshmallow Bath Towel': 'マシュマロ バスタオル',
    'Marshmallow Face Towel': 'マシュマロ フェイスタオル',
    'The Marshmallow Towel 2.0 Family Pack': 'マシュマロタオル 2.0 ファミリーパック',
    'The Marshmallow Towel 2.0 Partner Pack': 'マシュマロタオル 2.0 ペアパック',
    'The Marshmallow Towel 2.0 Pack': 'マシュマロタオル 2.0 セット',
    'The Marshmallow Bath Towel 2.0': 'マシュマロ バスタオル 2.0',
    'The Marshmallow Face Towel 2.0': 'マシュマロ フェイスタオル 2.0',
    'The Marshmallow Bath Mat 2.0': 'マシュマロ バスマット 2.0',
    'Anniversary Sale': 'アニバーサリーセール',
    'Pack Savings': 'セット割引',
    'Pack Discount': 'セット割引',
    'All Products': 'すべての商品',
    'See Towel Packs': 'タオルセットを見る',
    'Cloud soft comfort': '雲のようなやわらかさ',
    'Fast lightweight absorbency': '軽やかでスムーズな吸水性',
    'Engineered to stay plush': 'ふんわり感が続く設計',
    'Thoughtfully sourced': '素材と質感へのこだわり',
    '30 Day Returns': '30日間の返品ポリシー',
    'Swipe to explore': 'スワイプして見る',
    'In two sizes - face and bath.': 'フェイスとバス、2つのサイズ。'
  };
  const JP_PAGE_PAIRS = {
    home: [
      ['Introducing the new', '日本上陸、マシュマロタオル', 'Welcome to Mallo Japan'],
      ['Turn every shower into a spa-like escape with plush texture and lasting softness.', '毎日のバスタイムに、雲に包まれるような心地よさを。ふんわりとした立体的な質感で、お風呂上がりのひとときを特別に。', 'Make every shower feel a little more luxurious with our cloud-soft Marshmallow texture.'],
      ['FROM THE MALLO COMMUNITY', 'Malloを愛用する皆さまから', 'FROM OUR COMMUNITY'],
      ['Soft is a promise we keep, wash after wash.', '何度洗っても、心地よい肌ざわりを。', 'Softness you can enjoy, wash after wash.'],
      ['Five new colorways. While stocks last.', 'お気に入りのカラーを見つけて。数量限定。', 'Discover your favourite shade. Limited quantities.'],
      ["The same cloud-soft Marshmallow ridge, in a run of colors that won't stick around — grab your favorite before it's gone.", '人気のマシュマロタオルに、特別なカラーが登場。お気に入りの色をお早めに。', 'Our signature Marshmallow texture in special-release shades. Find your favourite while available.'],
      ['Plush, velvety fibers feel gentle against the skin, turning an everyday dry-off into a softer moment.', 'ベルベットのようなやさしい肌ざわりで、毎日のお風呂上がりを心地よいひとときに。', 'Velvety-soft fibres bring extra comfort to your everyday routine.'],
      ['Signature ridges pull moisture away quickly, without the heavy, bulky feel of traditional towels.', '立体的なリッジ構造が水分をすばやく吸収。かさばりにくく、軽やかな使い心地です。', 'Our signature ridges absorb moisture quickly without the bulky feel of a traditional towel.'],
      ['Machine washable. Quick to dry, and designed to stay soft through the routines of everyday use.', '洗濯機で洗えて、乾きやすい設計。毎日の使いやすさにもこだわりました。', 'Machine washable, quick-drying and designed for everyday use.'],
      ['From Marshmallow ridges to the refined Chevron weave, every texture is built to look distinctive and feel genuinely good.', 'マシュマロの立体感も、シェブロンの洗練された織り柄も。デザインと心地よさの両方を大切にしています。', 'From Marshmallow ridges to refined Chevron patterns, every texture is designed for comfort.'],
      ['More than just marshmallows', 'マシュマロだけじゃない、Malloの世界。', 'More than just Marshmallow towels'],
      ["Chevron towels, bath bombs, soap, and the softest little pumpkins you'll ever squeeze — the wider Mallo world, curated.", 'シェブロンタオルやバスアイテムなど、日常を彩るMalloのアイテムをチェック。', 'Discover Chevron towels and little comforts from the wider Mallo collection.'],
      ['Cloud-soft, ridge weave', '雲のようなやわらかさと立体的な織り', 'Cloud-soft, sculpted ridges'],
      ['Lightweight on skin', '軽やかな肌ざわり', 'Lightweight against your skin'],
      ['We made the towel we wanted to use.', '自分たちが毎日使いたいタオルを。', 'We made the towel we wanted to use.'],
      ['Too many towels looked good on the shelf but felt rough, heavy, or ordinary at home. So we started with the one thing that actually matters — how it feels the second you pick it up.', '見た目だけではなく、手に取った瞬間のやわらかさまで大切にしたい。そんな想いからMalloは生まれました。', 'Beautiful towels should feel beautiful, too. That is where Mallo began.'],
      ['That idea became the Marshmallow Series, known for its cloud-like ridges. The Chevron Series brings the same comfort in a more refined pattern, and Pure Cotton reimagines the classic in a plush, elevated finish.', 'ふっくらとした立体的なマシュマロシリーズから、洗練されたシェブロン、上質なピュアコットンまで。日常に小さな贅沢をお届けします。', 'Explore signature Marshmallow ridges, refined Chevron patterns and our plush Pure Cotton collection.'],
      ['Two takes on the', '好みに合わせて選べる', 'Two kinds of'],
      ['The Marshmallow Ridge, woven in', 'マシュマロのような立体感', 'The signature Marshmallow texture'],
      ['A towel brand bold enough to make everyday comfort extraordinary.', '毎日の「気持ちいい」を、少し特別に。', 'Everyday comfort, made a little more special.'],
      ['SPOILED', 'やわらかさに', 'SPOILED'],
      ['BY SOFTNESS', '包まれる幸せ', 'BY SOFTNESS'],
      ['SHOP WITH CONFIDENCE', '安心してお買い物', 'SHOP WITH CONFIDENCE']
    ],
    'pages/the-marshmallow-towel': [
      ['Introducing the NEW', '新しくなった', 'Introducing the NEW'],
      ['THICKER, SOFTER, AND FLUFFIER THAN EVER', 'もっとふんわり、もっと心地よく。', 'THICKER, SOFTER, AND FLUFFIER THAN EVER'],
      ['the ordinary shower towel reimagined', 'お風呂上がりの心地よさを、新しい定番に。', 'Make every post-shower moment softer.'],
      ['REDEFINE YOUR SHOWER EXPERIENCE', '毎日のバスタイムを、もっと特別に。', 'Make every shower feel special.'],
      ['Experience the ultimate comfort and cuteness with our Marshmallow Towel collection. Crafted with plush materials and adorned with pastel colors, these towels are designed to make every post-shower a delightful experience.', 'マシュマロのようなやわらかさと、かわいらしいカラー。毎日のお風呂上がりが楽しみになるタオルです。', 'Beautiful colours, plush texture and a wonderfully soft feel. Meet your new favourite towel.']
    ],
    'pages/pure-cotton-collection': [
      ['Pure Cotton Collection', 'ピュアコットン コレクション', 'Pure Cotton Collection'],
      ['The luxury of 100% cotton, reimagined with a softer handfeel, plush texture, and elevated finish.', '綿100%の心地よさを、もっと贅沢に。ふんわり厚みのある質感を楽しめるコレクション。', '100% long-staple cotton, reimagined with a beautifully soft, plush feel.'],
      ['100% Pure Cotton', '綿100%', '100% Pure Cotton'],
      ['Premium long-staple cotton.', '上質な長繊維綿を使用。', 'Premium long-staple cotton.'],
      ['The perfect weight for everyday use.', 'ふっくらとした430 GSMの厚み。', 'Plush 430 GSM construction.'],
      ['Engineered for maximum softness.', 'やわらかさを追求した独自構造。', 'Designed for beautiful softness.'],
      ['Low-Lint Weave', '毛羽落ちを抑える織り', 'Low-Lint Weave'],
      ['Style Your Space', 'バスルームを、自分らしく。', 'Style Your Space'],
      ['Softness you can feel.', '触れた瞬間に伝わるやわらかさ。', 'Softness you can feel.']
    ],
    'pages/the-chevron-towel': [
      ['DISCOVER CHEVRON', 'シェブロンシリーズ', 'DISCOVER CHEVRON'],
      ['The Chevron Towel does it all.', 'デザインも心地よさも、妥協しない。', 'Beautiful texture, everyday comfort.'],
      ['Transform your daily routine with the iconic Chevron Towel. Combining style and sophistication, these towels boast chic pastel hues that elevate any bathroom decor. Crafted from premium materials, the Chevron Towel is highly absorbent and luxuriously soft, ensuring a spa-like experience every time you step out of the shower. Indulge in the unparalleled quality and comfort that makes the Chevron Towel a must-have addition to your home.', '立体的なシェブロン柄と上品なカラーが、バスルームをさりげなく彩ります。やわらかさと吸水性にもこだわった、毎日使いたくなるタオルです。', 'Sculpted chevron texture, elegant colours, and everyday softness. A beautiful upgrade for your bathroom.']
    ],
    'collections/all-1': [
      ['All Products', 'Malloの商品を探す', 'Explore Mallo Japan'],
      ['Up to 40% Off Sitewide', 'お気に入りのタオルを見つけよう', 'Find your new favourite towels'],
      ['The Marshmallow Series you love, newly improved for even more softness and plush comfort.', '人気のマシュマロシリーズが、さらにふんわり心地よく進化しました。', 'Meet our newly improved, cloud-soft Marshmallow series.'],
      ['Limited-edition Marshmallow Towel releases, made in special colors for a short time only.', '限定カラーで楽しむマシュマロタオル。お気に入りをお見逃しなく。', 'Special-release Marshmallow colours, available for a limited time.']
    ],
    'collections/all': [['All Products', 'Malloの商品を探す', 'Explore Mallo Japan']],
    'pages/about-us': [['Towels that feel good, every day.', '毎日に、心地よいタオルを。', 'Towels that feel good, every day.']],
    'pages/contact': [
      ["Whether you have a question about our products, need assistance with an order, or just want to say hello, we're here and ready to help. Your satisfaction is our top priority, and we're dedicated to providing you with exceptional customer service.", '商品やご注文についてのご質問は、お気軽にお問い合わせください。日本からのお客様もサポートいたします。', 'Have a question about our towels or your order in Japan? We are happy to help.']
    ],
    'pages/partners': [
      ['Softness, made for sharing.', 'やわらかさを、大切な人へ。', 'Softness, made for sharing.'],
      ["Let's make something soft together.", '一緒に、心に残る贈り物を。', 'Let’s create something memorable.']
    ],
    'blogs/mallo-living': [
      ['Thoughtful guides on towels, fabric care, bathroom rituals, softness, wellness, and the small details that make everyday living feel more comfortable.', 'タオルのお手入れ、バスルームのアイデア、やわらかさの秘密。毎日をもっと心地よくするヒントをお届けします。', 'Towel-care tips, bathroom ideas and simple ways to make everyday living softer.']
    ],
    'pages/faqs': [
      ['Are your towels absorbent?', 'タオルの吸水性は高いですか？', 'Are your towels absorbent?'],
      ['Yes! They are highly absorbent.', 'はい。吸水性にこだわって設計されています。', 'Yes. Our towels are designed to be absorbent.'],
      ['Are your towels machine washable?', '洗濯機で洗えますか？', 'Are your towels machine washable?'],
      ['Yes! They can be machine washed.', 'はい。洗濯機で洗えます。詳しくは商品のお手入れ表示をご確認ください。', 'Yes. Please follow the care label for your product.'],
      ['Can your towels be tumble dried?', '乾燥機は使えますか？', 'Can your towels be tumble dried?'],
      ['Yes! They can be tumble dried.', '商品ごとのお手入れ表示をご確認ください。', 'Please follow the care label for your product.']
    ]
  };

  const JP_PANELS = {
    home: {
      en: ['HELLO, JAPAN', 'A little everyday luxury, now for Japan.', 'Meet the Marshmallow Towel 2.0: irresistibly cloud-soft texture, charming colours, and a lightweight feel for your everyday routine.', ['Cloud-soft comfort', 'Beautiful bathroom colours', 'Lightweight & easy to care for'], 'Explore Marshmallow Towels', 'pages/the-marshmallow-towel'],
      ja: ['日本の皆さま、こんにちは', '毎日のお風呂上がりに、マシュマロのような幸せを。', '見た目も肌ざわりも、とびきりやさしく。ふっくらとした立体感と軽やかな使い心地で、いつものバスタイムをちょっと特別に。', ['雲のようなやわらかさ', 'バスルームを彩るカラー', '軽くてお手入れ簡単'], 'マシュマロタオルを見る', 'pages/the-marshmallow-towel']
    },
    'pages/the-marshmallow-towel': {
      en: ['THE JAPAN EDIT', 'Your new favourite post-bath feeling.', 'Discover beautifully sculpted, cloud-soft Marshmallow ridges. Lightweight and easy to care for, the 2.0 series brings everyday comfort to your bathroom.', ['Signature plush texture', 'Lightweight feel', 'Designed for everyday use'], 'Browse all towels', 'collections/all-1'],
      ja: ['マシュマロタオル 2.0', 'お風呂上がりの時間が、もっと好きになる。', 'ぽこぽことした立体的な質感と、思わず触れたくなるやわらかさ。軽やかでお手入れしやすい、毎日使いたいタオルです。', ['ふんわり立体的な肌ざわり', '軽い使い心地', '毎日のお手入れも簡単'], 'すべてのタオルを見る', 'collections/all-1']
    },
    'pages/pure-cotton-collection': {
      en: ['100% LONG-STAPLE COTTON', 'A hotel-inspired feeling at home.', 'Our Pure Cotton collection brings beautiful Marshmallow texture to 100% long-staple cotton, with plush 430 GSM CloudLoom™ construction.', ['100% cotton', '430 GSM', 'Plush, comforting texture']],
      ja: ['上質な綿100%', 'おうちで楽しむ、ホテルのような心地よさ。', '長繊維綿100%のピュアコットンシリーズ。430 GSMのふっくらとした厚みと、CloudLoom™ならではのやさしい肌ざわりを。', ['綿100%', '430 GSM', '贅沢なふんわり感']]
    },
    'pages/the-chevron-towel': {
      en: ['TEXTURE MEETS STYLE', 'A little more beauty in every bathroom.', 'Add a refined touch with sculpted chevron patterning and comfortable softness.', ['Beautiful sculpted pattern', 'Soft to the touch', 'Everyday comfort']],
      ja: ['デザインも、心地よさも。', 'バスルームを彩る、洗練されたタオル。', '立体的なシェブロン柄と心地よい肌ざわり。毎日の暮らしにさりげない上質感をプラスします。', ['美しい立体的な柄', 'やさしい肌ざわり', '毎日の使いやすさ']]
    },
    'collections/all-1': {
      en: ['WELCOME TO MALLO JAPAN', 'Find your kind of soft.', 'Explore our signature Marshmallow 2.0, luxurious Pure Cotton, Chevron and limited-release shades.', ['Signature Plush', 'Pure Cotton', 'Limited colours']],
      ja: ['MALLO JAPAN', 'あなた好みの、やわらかさを。', '軽やかなマシュマロ 2.0、ふっくらしたピュアコットン、洗練されたシェブロン。お気に入りの一枚を見つけてください。', ['シグネチャープラッシュ', 'ピュアコットン', '限定カラー']]
    },
    'collections/all': {
      en: ['WELCOME TO MALLO JAPAN', 'Find your kind of soft.', 'Beautifully textured towels, charming colours and a little everyday luxury.'],
      ja: ['MALLO JAPAN', 'あなた好みの、やわらかさを。', 'ふんわりしたタオルと美しいカラーで、毎日に小さな贅沢を。']
    },
    'pages/about-us': {
      en: ['FROM MALLO TO JAPAN', 'Small moments deserve a softer touch.', 'Mallo creates distinctive towels that make everyday routines more comfortable and beautiful. Welcome to our community.'],
      ja: ['MALLOについて', 'いつもの毎日に、少しの贅沢を。', '毎日使うものだから、見た目だけでなく肌ざわりにもこだわりたい。Malloはそんな想いから生まれたタオルブランドです。']
    },
    'pages/faqs': {
      en: ['SHOPPING FROM JAPAN?', 'Here is what to know.', 'Delivery options, shipping charges, available payments and any customs-related costs depend on the details of your order and checkout.'],
      ja: ['日本からのご注文について', 'ご購入前にご確認ください。', '配送料や利用できるお支払い方法、配送条件は、ご注文内容とチェックアウト画面の表示をご確認ください。']
    },
    'pages/contact': {
      en: ['QUESTIONS FROM JAPAN?', 'We are happy to help.', 'For help with products, delivery or orders, please email our team and include your delivery prefecture when asking about shipping.', null, 'Email Mallo Support', null, 'mailto:support@mallotowels.com'],
      ja: ['日本からのお問い合わせ', 'お気軽にご相談ください。', '商品やご注文、配送についてご不明な点は、お気軽にお問い合わせください。配送に関するご質問では都道府県もお知らせください。', null, 'メールでお問い合わせ', null, 'mailto:support@mallotowels.com']
    },
    'pages/partners': {
      en: ['JAPAN PARTNERSHIPS', 'Thoughtful gifting, beautiful comfort.', 'Looking for towels for corporate gifts, hospitality or retail in Japan? Tell us more about your project and estimated quantities.', ['Corporate & employee gifts', 'Hotels & hospitality', 'Retail & special events']],
      ja: ['法人・パートナーのお客様へ', '心地よさを贈る、新しい選択肢。', '企業ギフト、ホテル・宿泊施設、小売販売など、日本でのパートナーシップについてお気軽にご相談ください。', ['企業・従業員向けギフト', 'ホテル・宿泊施設', '小売・イベント向け']]
    },
    'collections/limited-edition-releases': {
      en: ['LIMITED-RELEASE COLOURS', 'Find your favourite before it is gone.', 'Discover our signature cloud-soft texture in limited-release shades.'],
      ja: ['限定カラー', 'お気に入りの色を、お見逃しなく。', 'いつものマシュマロのやわらかさを、特別なカラーでお楽しみください。']
    },
    'collections/bath-essentials': {
      en: ['BATH ESSENTIALS', 'Small comforts, every day.', 'Explore little ways to make your bathroom routine feel more special. Available items are determined at checkout.'],
      ja: ['バスアイテム', 'バスタイムに、小さな楽しみを。', '毎日のバスルームを心地よくするアイテムをご紹介します。配送対象商品はチェックアウト時にご確認ください。']
    },
    'blogs/mallo-living': {
      en: ['THE MALLO JOURNAL', 'Softer living starts with little things.', 'Explore practical towel-care tips, bathroom inspiration and everyday comfort.'],
      ja: ['MALLO JOURNAL', '毎日の暮らしを、もっと心地よく。', 'タオルのお手入れ方法やバスルームのヒントなど、日常の小さな楽しみをご紹介します。']
    },
    'policies/shipping-policy': {
      en: ['SHIPPING TO JAPAN', 'Please check delivery terms at checkout.', 'Delivery options, shipping charges and any possible customs charges depend on your order and address. For clarification before ordering, contact our support team.', null, 'Contact support', 'pages/contact'],
      ja: ['日本への配送', '送料・配送条件をご確認ください。', '送料、配送方法、関税・輸入消費税などの費用は、ご注文内容や配送先によって異なる場合があります。ご不明な点はご注文前にお問い合わせください。', null, 'お問い合わせ', 'pages/contact']
    },
    'policies/refund-policy': {
      en: ['RETURNS FROM JAPAN', 'Please contact us before returning an item.', 'The published return policy and its eligibility conditions apply. International return postage may be required. Contact support for instructions.', null, 'Contact support', 'pages/contact'],
      ja: ['日本からの返品について', '返送前にお問い合わせください。', '返品・返金には下記のポリシーに定める条件が適用されます。海外返送の送料が発生する場合があります。返送前にサポートまでご連絡ください。', null, 'お問い合わせ', 'pages/contact']
    },
    cart: {
      en: ['JAPAN CHECKOUT', 'Delivery and payment options appear at checkout.', 'Currency conversion is handled by the store automatically. Please verify the final shipping costs and available payment methods at checkout.'],
      ja: ['日本からのご注文', '送料とお支払い方法をご確認ください。', '通貨表示はショップの設定に従います。送料やご利用いただけるお支払い方法はチェックアウト画面でご確認ください。']
    },
    'pages/the-marshmallow-towel-a-gift-for-her': {
      en: ['THE GIFT OF SOFTNESS', 'A thoughtful little gift, beautifully soft.', 'Treat someone you love to the cloud-soft Marshmallow texture and colours made to brighten their daily routine.'],
      ja: ['やわらかさを贈ろう', '毎日に寄り添う、心地よい贈り物。', 'ふんわりとしたマシュマロタオルを、大切な人へ。日常をちょっと特別にするギフトに。']
    }
  };
  /* Collections not requiring a standalone unique panel share a neutral one. */
  const JP_COLLECTION_FALLBACK = {
    en: ['EXPLORE MALLO', 'Find your favourite towels.', 'Discover our Marshmallow textures and colours. Choose the style that fits your daily routine.'],
    ja: ['MALLOのコレクション', 'お気に入りのタオルを見つけて。', 'やわらかさも、デザインも、あなた好みの一枚を。']
  };
  const JP_FAQS = {
    en: [
      ['Can I order from Japan?', 'Select Japan as your destination, enter your address and review the options at checkout. Please contact us if you need help.'],
      ['What will shipping to Japan cost?', 'Shipping charges and available services depend on your address and order. Check the final checkout amount.'],
      ['How long does shipping to Japan take?', 'Delivery times depend on the carrier, dispatch arrangements and your address. Contact support if you need more detail.'],
      ['Are import duties or taxes charged?', 'International orders can incur customs duties, import consumption tax or clearance charges. Contact our team if you need to confirm how they are handled.'],
      ['Can I pay with Japanese payment methods?', 'Available payment options are displayed at checkout. We cannot promise a particular wallet, convenience-store payment or card unless it appears there.'],
      ['What is the difference between Signature Plush and Pure Cotton?', 'Signature Plush uses an 85% polyester / 15% nylon microfibre blend for a lightweight, cloud-soft feel. Pure Cotton uses 100% long-staple cotton at 430 GSM for a denser, hotel-inspired feel.'],
      ['How do returns from Japan work?', 'The published Returns Policy applies, including eligibility restrictions for sale items. Contact support before shipping anything back internationally.']
    ],
    ja: [
      ['日本から注文できますか？', '配送先に「日本」を選び、住所を入力すると、利用できる配送方法がチェックアウト画面に表示されます。ご不明な点はお問い合わせください。'],
      ['日本への送料はいくらですか？', '送料はご注文内容や配送先によって異なります。最終的な金額はチェックアウト画面でご確認ください。'],
      ['配送にはどのくらいかかりますか？', 'お届け日数は配送先や配送業者、出荷状況によって異なります。詳しくはサポートまでお問い合わせください。'],
      ['関税や輸入消費税はかかりますか？', '海外からの配送には、関税・輸入消費税・通関手数料などが発生する場合があります。負担方法についてはご購入前にお問い合わせください。'],
      ['日本の決済方法は使えますか？', 'ご利用いただける決済方法はチェックアウト画面に表示されます。特定の電子決済、コンビニ払い、クレジットカード等の対応は表示内容をご確認ください。'],
      ['シグネチャープラッシュとピュアコットンの違いは？', 'シグネチャープラッシュはポリエステル85%・ナイロン15%のマイクロファイバー生地を使用し、軽さと雲のようなやわらかさが特徴です。ピュアコットンは長繊維綿100%・430 GSMで、よりふっくらとした質感です。'],
      ['日本から返品できますか？', '返品にはサイト掲載の返品ポリシーが適用され、セール品など対象外の商品があります。海外から返送する前にサポートまでお問い合わせください。']
    ]
  };

  function japanTextTransform(text, mode, region) {
    const trimmed = normalized(text);
    if (!trimmed) return text;
    const table = mode === 'ja' ? 1 : 2;
    const pairs = JP_PAGE_PAIRS[pageKey] || [];
    const match = pairs.find(p => normalized(p[0]) === trimmed);
    if (match) return match[table];
    if (mode === 'ja' && Object.prototype.hasOwnProperty.call(JP_COMMON, trimmed)) return JP_COMMON[trimmed];
    // Footer/header only: never copy US-dollar free-shipping thresholds into Japan.
    if (region === 'chrome') {
      const banner = JP_BANNER[mode];
      if (Object.prototype.hasOwnProperty.call(banner, trimmed)) return banner[trimmed];
      let translated = text;
      for (const [from, to] of Object.entries(banner)) {
        if (translated.includes(from)) translated = translated.split(from).join(to);
      }
      return translated;
    }
    if (trimmed === 'Free shipping on orders over $99.') {
      return mode === 'ja' ? '送料はチェックアウト時にご確認ください。' : 'Shipping options appear at checkout.';
    }
    return text;
  }

  function translateJapanTree(root, mode, region) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach(textNode => {
      if (shouldSkip(textNode.parentElement)) return;
      // Legal clauses, third-party review widgets and cart totals should not be
      // client-side rewritten. Policy panels are separately bilingual.
      if (region === 'main' && (pageKey.startsWith('policies/') || pageKey === 'cart')) return;
      if (textNode.parentElement.closest && textNode.parentElement.closest('[data-mallo-jp],.jdgm-rev,.loox-review,.okeReviews,.spr-review,blockquote,[data-review-id],.product-price,.cart-totals')) return;
      const previous = JP_ORIGINALS.get(textNode);
      const original = previous && previous.rendered === textNode.nodeValue ? previous.original : textNode.nodeValue;
      const translated = japanTextTransform(original, mode, region);
      JP_ORIGINALS.set(textNode, { original, rendered: translated });
      if (translated !== textNode.nodeValue) textNode.nodeValue = translated;
    });
  }

  function injectJapanStyles() {
    if (document.getElementById('mallo-jp-v4-css')) return;
    const style = document.createElement('style');
    style.id = 'mallo-jp-v4-css';
    style.textContent = `
      [data-mallo-jp] { box-sizing: border-box; }
      .mallo-jp-panel { width:100%; background:linear-gradient(118deg,#fff8f9,#fbeceb 55%,#f4effa); color:#203151; padding:clamp(24px,4vw,54px) 20px; }
      .mallo-jp-inner { max-width:1160px; margin:auto; display:flex; gap:30px; justify-content:space-between; align-items:center; }
      .mallo-jp-copy { max-width:820px; }
      .mallo-jp-eyebrow { display:block; color:#72629d; letter-spacing:.1em; font-size:12px; font-weight:700; margin:0 0 12px; }
      .mallo-jp-heading { color:#203151; font-size:clamp(23px,2.55vw,36px); line-height:1.35; font-weight:650; letter-spacing:-.025em; margin:0 0 12px; }
      .mallo-jp-body { color:#3e4e6e; font-size:clamp(14px,1.12vw,16px); line-height:1.85; margin:0; }
      .mallo-jp-benefits { display:flex; flex-wrap:wrap; gap:8px; padding:0; margin:17px 0 0; list-style:none; }
      .mallo-jp-benefits li { font-size:12px; font-weight:600; padding:7px 13px; border:1px solid #ded6eb; background:#ffffffb3; border-radius:22px; }
      .mallo-jp-cta { display:inline-flex; align-items:center; text-align:center; justify-content:center; white-space:nowrap; min-height:44px; padding:12px 20px; border-radius:90px; background:#203151; color:#fff!important; text-decoration:none!important; font-size:13px; font-weight:700; }
      .mallo-jp-panel--simple { padding:22px 20px; background:#faf8fd; border-block:1px solid #e9e3f1; }
      .mallo-jp-panel--simple .mallo-jp-heading { font-size:clamp(19px,2vw,27px); }
      .mallo-jp-questions { max-width:980px; margin:30px auto; padding:0 20px; }
      .mallo-jp-questions h2 { color:#203151; font-size:clamp(20px,2.3vw,30px); margin:0 0 18px; }
      .mallo-jp-questions details { border-bottom:1px solid #e8e5ee; padding:15px 0; }
      .mallo-jp-questions summary { cursor:pointer; color:#203151; font-weight:650; font-size:15px; }
      .mallo-jp-questions p { font-size:14px; line-height:1.8; color:#3e4e6e; margin:10px 0 0; }
      .mallo-jp-product { background:#fbf7fb; color:#203151; border:1px solid #e8dff0; border-radius:14px; margin:18px 0; padding:16px 18px; max-width:720px; }
      .mallo-jp-product strong { display:block; margin-bottom:7px; font-size:13px; }
      .mallo-jp-product ul { margin:0; padding:0; list-style:none; display:flex; gap:8px 20px; flex-wrap:wrap; }
      .mallo-jp-product li { font-size:12px; line-height:1.5; }
      .mallo-jp-product li::before { content:'✓'; margin-right:7px; color:#72629d; font-weight:700; }
      .mallo-jp-toggle { position:fixed; right:max(14px,env(safe-area-inset-right)); bottom:calc(18px + env(safe-area-inset-bottom,0px)); z-index:55; display:inline-flex; gap:3px; padding:4px; background:#fff; border:1px solid #dbdce8; border-radius:99px; box-shadow:0 5px 24px #20315124; font-family:inherit; }
      .mallo-jp-toggle button { border:0; border-radius:40px; background:transparent; padding:9px 13px; min-width:67px; cursor:pointer; font-size:12px; font-weight:700; color:#203151; line-height:1.2; }
      .mallo-jp-toggle button[aria-pressed="true"] { background:#203151; color:#fff; }
      .mallo-jp-toggle button:focus-visible { outline:2px solid #72629d; outline-offset:3px; }
      @media(max-width:749px) { .mallo-jp-inner { display:block; } .mallo-jp-panel { padding:28px 18px; } .mallo-jp-panel--simple { padding:19px 16px; } .mallo-jp-cta { margin-top:16px; white-space:normal; } .mallo-jp-toggle { bottom:calc(14px + env(safe-area-inset-bottom,0px)); right:10px; } }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  function JapanPanelInfo() {
    const entries = JP_PANELS[pageKey] || (pageKey.startsWith('collections/') ? JP_COLLECTION_FALLBACK : null);
    return entries && entries[japanLanguage];
  }
  function populateJapanPanel(panel, values, isSimple) {
    panel.textContent = '';
    panel.setAttribute('lang', japanLanguage);
    panel.setAttribute('data-mallo-language', japanLanguage);
    const inner = el('div', 'mallo-jp-inner');
    const copy = el('div', 'mallo-jp-copy');
    if (values[0]) copy.appendChild(el('span', 'mallo-jp-eyebrow', values[0]));
    if (values[1]) copy.appendChild(el('h2', 'mallo-jp-heading', values[1]));
    if (values[2]) copy.appendChild(el('p', 'mallo-jp-body', values[2]));
    if (Array.isArray(values[3]) && values[3].length) {
      const ul = el('ul', 'mallo-jp-benefits');
      values[3].forEach(value => ul.appendChild(el('li', '', value)));
      copy.appendChild(ul);
    }
    inner.appendChild(copy);
    if (values[4]) {
      const a = el('a', 'mallo-jp-cta', values[4]);
      a.href = values[6] || href(values[5]);
      inner.appendChild(a);
    }
    panel.appendChild(inner);
  }
  function mountJapanPanel(main) {
    const info = JapanPanelInfo();
    if (!main || !info) return;
    let panel = main.querySelector('[data-mallo-jp="page-panel"]');
    const isSimple = pageKey.startsWith('policies/') || pageKey === 'cart';
    if (!panel) {
      const hostHeading = main.querySelector('h1');
      const headingSection = hostHeading && hostHeading.closest('.shopify-section');
      const host = headingSection && headingSection.parentElement === main ? headingSection : main.firstElementChild;
      if (!host) return;
      panel = el('section', 'mallo-jp-panel' + (isSimple ? ' mallo-jp-panel--simple' : ''));
      panel.setAttribute('data-mallo-jp', 'page-panel');
      panel.setAttribute('aria-label', 'Mallo Japan information');
      if (isSimple) host.before(panel);
      else host.after(panel);
    }
    if (panel.getAttribute('data-mallo-language') !== japanLanguage) populateJapanPanel(panel, info, isSimple);
  }
  function mountJapanFAQs(main) {
    if (pageKey !== 'pages/faqs' || !main) return;
    let container = main.querySelector('[data-mallo-jp="faq-questions"]');
    if (!container) {
      container = el('section', 'mallo-jp-questions');
      container.setAttribute('data-mallo-jp', 'faq-questions');
      main.appendChild(container);
    }
    if (container.getAttribute('data-mallo-language') === japanLanguage) return;
    container.textContent = '';
    container.setAttribute('lang', japanLanguage);
    container.setAttribute('data-mallo-language', japanLanguage);
    container.appendChild(el('h2', '', japanLanguage === 'ja' ? '日本からのご注文：よくあるご質問' : 'Shopping from Japan — FAQs'));
    for (const [question, answer] of JP_FAQS[japanLanguage]) {
      const details = el('details');
      details.appendChild(el('summary', '', question));
      details.appendChild(el('p', '', answer));
      container.appendChild(details);
    }
    const a = el('a', 'mallo-jp-cta', japanLanguage === 'ja' ? 'お問い合わせはこちら' : 'Contact our team');
    a.href = href('pages/contact');
    container.appendChild(a);
  }
  function mountJapanProductBenefits(main) {
    if (!main || !pageKey.startsWith('products/')) return;
    const heading = main.querySelector('h1');
    if (!heading) return;
    const productType = normalized(heading.textContent + ' ' + pageKey).toLowerCase();
    if (!/towel|marshmallow|chevron/.test(productType) || /soap|pumpkin|bomb/.test(productType)) return;
    const isCotton = /cotton|コットン/.test(productType) && !/2\.0/.test(productType);
    const isChevron = /chevron|シェブロン/.test(productType);
    let panel = main.querySelector('[data-mallo-jp="product-benefits"]');
    if (!panel) {
      panel = el('aside', 'mallo-jp-product');
      panel.setAttribute('data-mallo-jp', 'product-benefits');
      const parent = heading.closest('.product-information__content,.product-details,.product__info-wrapper,.product-info,.product-information');
      if (parent && !parent.closest('form')) parent.appendChild(panel);
      else {
        const section = heading.closest('.shopify-section');
        if (section && section.parentElement === main) section.after(panel);
        else main.appendChild(panel);
      }
    }
    if (panel.getAttribute('data-mallo-language') === japanLanguage) return;
    panel.textContent = '';
    panel.setAttribute('data-mallo-language', japanLanguage);
    panel.setAttribute('lang', japanLanguage);
    const mode = japanLanguage;
    const features = isCotton ? {
      ja: ['綿100%のふっくらした肌ざわり', '430 GSMの贅沢な厚み', 'CloudLoom™構造'],
      en: ['100% long-staple cotton', 'Plush 430 GSM feel', 'CloudLoom™ construction']
    } : isChevron ? {
      ja: ['立体的なシェブロン柄', 'やさしい肌ざわり', '毎日使いたい心地よさ'],
      en: ['Sculpted chevron design', 'Soft & comfortable', 'Made for everyday use']
    } : {
      ja: ['ふんわりとしたマシュマロの質感', '軽くて乾きやすい設計', '毎日のお手入れも簡単'],
      en: ['Signature cloud-soft ridges', 'Lightweight, quick-drying design', 'Easy everyday care']
    };
    panel.appendChild(el('strong', '', mode === 'ja' ? '毎日をちょっと心地よく。' : 'A little more softness for everyday life'));
    const ul = el('ul');
    features[mode].forEach(text => ul.appendChild(el('li', '', text)));
    panel.appendChild(ul);
  }

  function mountJapanToggle() {
    let wrap = document.querySelector('[data-mallo-jp="language-toggle"]');
    if (!wrap) {
      wrap = el('div', 'mallo-jp-toggle');
      wrap.setAttribute('data-mallo-jp', 'language-toggle');
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', 'Japan website language / 日本語表示');
      for (const [code, label] of [['ja', '日本語'], ['en', 'English']]) {
        const button = el('button', '', label);
        button.type = 'button';
        button.setAttribute('data-mallo-jp-lang', code);
        button.addEventListener('click', () => setJapanLanguage(code));
        wrap.appendChild(button);
      }
      document.body.appendChild(wrap);
    }
    const buttons = wrap.querySelectorAll('button');
    buttons.forEach(button => {
      const active = button.getAttribute('data-mallo-jp-lang') === japanLanguage;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }
  function setJapanLanguage(mode) {
    if (!JP || (mode !== 'ja' && mode !== 'en')) return;
    if (mode !== japanLanguage) japanLanguage = mode;
    persistJapanLanguage(mode);
    update();
  }
  function localiseJapan() {
    injectJapanStyles();
    const main = mainElement();
    // Use the *original* English content as source, even when toggled back
    // from Japanese. WeakMap snapshots make the control truly reversible.
    if (main) {
      mountJapanProductBenefits(main);
      translateJapanTree(main, japanLanguage, 'main');
      mountJapanPanel(main);
      mountJapanFAQs(main);
    }
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    const chrome = [header, footer];
    for (const item of chrome) translateJapanTree(item, japanLanguage, 'chrome');
    for (const item of document.querySelectorAll('[class*="announcement-bar"],[class*="announcement_bar"],[id*="announcement-bar"]')) {
      if (!header || !header.contains(item)) translateJapanTree(item, japanLanguage, 'chrome');
    }
    mountJapanToggle();
  }


  /* ==================== MEXICO LOCALISATION v5 ====================
   * MX -> Mexican Spanish by default; optional English toggle.
   * Targeted editorial translation, not checkout translation.
   * No changes to Shopify Markets, pricing, discounts, inventory, cart,
   * payment methods, customer data, shipping charges or tax calculations.
   */
  const MX_STORAGE_KEY = 'mallo_mexico_language_v5';
  let mexicoLanguage = 'es';
  const MX_ORIGINALS = new WeakMap();

  function getMexicoLanguage() {
    const requested = new URLSearchParams(location.search).get('mallo_lang');
    if (requested === 'es' || requested === 'en') return requested;
    try {
      const saved = localStorage.getItem(MX_STORAGE_KEY);
      if (saved === 'es' || saved === 'en') return saved;
    } catch (_) { /* Private browsing may block storage. */ }
    return 'es';
  }
  function saveMexicoLanguage(lang) {
    try { localStorage.setItem(MX_STORAGE_KEY, lang); } catch (_) { /* optional */ }
  }

  /* Only promise an existing global offer when its exact message occurs in
     the source storefront. Never invent a Mexico-exclusive discount. */
  const MX_BANNER = {
    es: {
      'AUTUMN IS HERE 🍂': '¡HOLA, MÉXICO! 🇲🇽',
      'Enjoy 25% OFF with code AUTUMN25': '25% de descuento con el código AUTUMN25',
      'Free tracked shipping over $99': 'Consulta las opciones de envío al finalizar tu compra',
      'Free shipping on orders over $99.': 'Consulta el costo de envío al finalizar tu compra.',
      'Free Shipping on qualifying orders': 'Consulta las condiciones de envío al pagar',
      'Free Shipping On qualifying orders': 'Consulta las condiciones de envío al pagar'
    },
    en: {
      'AUTUMN IS HERE 🍂': 'HELLO, MEXICO! 🇲🇽',
      'Enjoy 25% OFF with code AUTUMN25': '25% OFF with code AUTUMN25',
      'Free tracked shipping over $99': 'Shipping options shown at checkout',
      'Free shipping on orders over $99.': 'Shipping options are shown at checkout.',
      'Free Shipping on qualifying orders': 'Shipping details shown at checkout',
      'Free Shipping On qualifying orders': 'Shipping details shown at checkout'
    }
  };

  /* Small, exact-match UI glossary. Preserve original product names, option
     values, SKU, prices and reviews to avoid breaking Horizon selectors. */
  const MX_COMMON = {
    'Shop': 'Tienda',
    'Shop All': 'Ver todos los productos',
    'SHOP ALL': 'VER TODOS LOS PRODUCTOS',
    'Shop Now': 'Comprar ahora',
    'SHOP NOW': 'COMPRAR AHORA',
    'Learn More': 'Conoce más',
    'Read our story': 'Conoce nuestra historia',
    'About': 'Nosotros',
    'About Us': 'Sobre Mallo',
    'Our Story': 'Nuestra historia',
    'Signature Plush': 'Signature Plush',
    'SIGNATURE PLUSH': 'SIGNATURE PLUSH',
    'Pure Cotton': 'Algodón puro',
    'PURE COTTON': 'ALGODÓN PURO',
    'The Chevron Series': 'Colección Chevron',
    'Chevron Series': 'Colección Chevron',
    'Limited Edition': 'Edición limitada',
    'LIMITED EDITION': 'EDICIÓN LIMITADA',
    'Bath Essentials': 'Esenciales de baño',
    'Choose Options': 'Elegir opciones',
    'Choose options': 'Elegir opciones',
    'Add to Cart': 'Agregar al carrito',
    'Add to cart': 'Agregar al carrito',
    'View cart': 'Ver carrito',
    'View Cart': 'Ver carrito',
    'View Product': 'Ver producto',
    'Checkout': 'Finalizar compra',
    'Check out': 'Finalizar compra',
    'Continue shopping': 'Seguir comprando',
    'Search': 'Buscar',
    'Log in': 'Iniciar sesión',
    'Best Sellers': 'Los más vendidos',
    'BESTSELLERS': 'LOS MÁS VENDIDOS',
    'SHOP OUR BESTSELLERS': 'CONOCE LOS FAVORITOS',
    'Material': 'Material',
    'Feel': 'Textura',
    'Absorbency': 'Absorción',
    'Best for': 'Ideal para',
    'Material:': 'Material:',
    'Fabric:': 'Tela:',
    'Color': 'Color',
    'Colour': 'Color',
    'Size': 'Tamaño',
    'Quantity': 'Cantidad',
    'Reviews': 'Reseñas',
    'Verified Buyer': 'Compra verificada',
    'VERIFIED BUYER': 'COMPRA VERIFICADA',
    'Verified': 'Verificado',
    'Free Shipping': 'Consulta las opciones de envío',
    'Secure Checkout': 'Pago seguro',
    'Easy Returns': 'Información sobre devoluciones',
    'Contact': 'Contacto',
    'Contact Us': 'Contáctanos',
    'FAQs': 'Preguntas frecuentes',
    'Shipping Policy': 'Política de envíos',
    'Refund Policy': 'Política de reembolsos',
    'Returns Policy': 'Política de devoluciones',
    'Read the journal': 'Leer artículos',
    'From the journal': 'Desde nuestro blog',
    'Our Philosophy': 'Nuestra filosofía',
    'Why Mallo': 'Por qué Mallo',
    'why mallo': 'Por qué Mallo',
    'Shop Signature Plush': 'Conoce Signature Plush',
    'Shop Pure Cotton': 'Conoce algodón puro',
    'Shop Bath Essentials': 'Ver esenciales de baño',
    'Shop Mallo': 'Conoce Mallo',
    'Shop Mallo Towels': 'Ver las toallas',
    'Anniversary Sale': 'Promoción de aniversario',
    'Pack Savings': 'Ahorra con paquetes',
    'Pack Discount': 'Descuento por paquete',
    'All Products': 'Todos los productos',
    'See Towel Packs': 'Ver paquetes de toallas',
    'Cloud soft comfort': 'Suavidad como de nube',
    'Fast lightweight absorbency': 'Ligereza y gran absorción',
    'Engineered to stay plush': 'Diseñadas para conservar su suavidad',
    'Thoughtfully sourced': 'Materiales elegidos con cuidado',
    '30 Day Returns': 'Consulta nuestra política de devoluciones',
    'Swipe to explore': 'Desliza para explorar',
    'In two sizes - face and bath.': 'En dos tamaños: facial y de baño.',
    'Sold out': 'Agotado',
    'Sale': 'Oferta',
    'New': 'Nuevo'
  };

  /* [exact source text, es-MX, localised English].
     Actual English strings audited from mallotowels.com and v4. */
  const MX_PAGE_PAIRS = {
    home: [
      ['Introducing the new', '¡HOLA, MÉXICO!', 'HELLO, MEXICO!'],
      ['Turn every shower into a spa-like escape with plush texture and lasting softness.',
        'Convierte cada baño en un momento especial con una textura acolchonadita, suavidad irresistible y colores que enamoran.',
        'Make every shower a little escape with irresistibly soft texture and beautiful colours.'],
      ['FROM THE MALLO COMMUNITY', 'LO QUE OPINA LA COMUNIDAD MALLO', 'FROM THE MALLO COMMUNITY'],
      ['Soft is a promise we keep, wash after wash.', 'Suavidad que se siente, lavado tras lavado.', 'Softness you can count on, wash after wash.'],
      ['Five new colorways. While stocks last.', 'Nuevos colores para enamorarte. Hasta agotar existencias.', 'Beautiful new shades. While supplies last.'],
      ["The same cloud-soft Marshmallow ridge, in a run of colors that won't stick around — grab your favorite before it's gone.",
        'Nuestra textura Marshmallow de siempre, ahora en tonos especiales por tiempo limitado. Elige tu favorito.',
        'The same cloud-soft Marshmallow texture in limited-release shades. Pick your favourite.'],
      ['Plush, velvety fibers feel gentle against the skin, turning an everyday dry-off into a softer moment.',
        'Fibras suaves como terciopelo que acarician tu piel y transforman lo cotidiano en un pequeño lujo.',
        'Plush, velvety fibres turn an everyday moment into a little luxury.'],
      ['Signature ridges pull moisture away quickly, without the heavy, bulky feel of traditional towels.',
        'Su textura con relieve absorbe la humedad rápidamente, sin sentirse pesada ni voluminosa.',
        'Signature ridges absorb moisture quickly, without the bulky feel of traditional towels.'],
      ['Machine washable. Quick to dry, and designed to stay soft through the routines of everyday use.',
        'Fáciles de lavar a máquina y de secado rápido. Diseñadas para consentirte todos los días.',
        'Machine washable, quick-drying and made for everyday comfort.'],
      ['From Marshmallow ridges to the refined Chevron weave, every texture is built to look distinctive and feel genuinely good.',
        'Desde la textura Marshmallow hasta el tejido Chevron, cada diseño se ve increíble y se siente aún mejor.',
        'From Marshmallow ridges to elegant Chevron patterns, every texture feels as good as it looks.'],
      ['More than just marshmallows', 'Mucho más que Marshmallow', 'More than Marshmallow'],
      ["Chevron towels, bath bombs, soap, and the softest little pumpkins you'll ever squeeze — the wider Mallo world, curated.",
        'Descubre nuestras toallas Chevron y más detalles para hacer de tu baño tu lugar favorito.',
        'Discover Chevron towels and more little comforts from Mallo.'],
      ['Cloud-soft, ridge weave', 'Textura esponjosa como nube', 'Cloud-soft sculpted ridges'],
      ['Lightweight on skin', 'Ligeras y suaves al tacto', 'Lightweight and soft to the touch'],
      ['We made the towel we wanted to use.', 'Creamos la toalla que queríamos usar.', 'We made the towel we wanted to use.'],
      ['Too many towels looked good on the shelf but felt rough, heavy, or ordinary at home. So we started with the one thing that actually matters — how it feels the second you pick it up.',
        'Una toalla puede verse bonita, pero también debe sentirse increíble. Por eso pusimos la suavidad en el centro de todo.',
        'A towel can look beautiful, but it should feel wonderful, too. That is where we began.'],
      ['That idea became the Marshmallow Series, known for its cloud-like ridges. The Chevron Series brings the same comfort in a more refined pattern, and Pure Cotton reimagines the classic in a plush, elevated finish.',
        'Así nació Marshmallow, con sus relieves suaves como nube. También creamos Chevron y Pure Cotton para que encuentres tu textura ideal.',
        'That idea became Marshmallow, followed by the Chevron and Pure Cotton collections.'],
      ['Two takes on the', 'Dos formas de disfrutar', 'Two ways to enjoy'],
      ['The Marshmallow Ridge, woven in', 'Nuestra textura Marshmallow', 'Signature Marshmallow texture'],
      ['A towel brand bold enough to make everyday comfort extraordinary.',
        'Toallas que hacen extraordinarios tus momentos de todos los días.',
        'Towels that make everyday comfort extraordinary.'],
      ['SHOP WITH CONFIDENCE', 'COMPRA CON CONFIANZA', 'SHOP WITH CONFIDENCE']
    ],
    'pages/the-marshmallow-towel': [
      ['Introducing the NEW', 'Descubre las nuevas', 'Introducing the NEW'],
      ['THICKER, SOFTER, AND FLUFFIER THAN EVER', 'MÁS SUAVES Y ESPONJOSAS QUE NUNCA', 'SOFTER AND FLUFFIER THAN EVER'],
      ['the ordinary shower towel reimagined', 'Tu nueva forma favorita de salir del baño', 'Your new favourite post-shower feeling'],
      ['REDEFINE YOUR SHOWER EXPERIENCE', 'REDESCUBRE LA SUAVIDAD', 'REDEFINE YOUR SHOWER EXPERIENCE'],
      ['Experience the ultimate comfort and cuteness with our Marshmallow Towel collection. Crafted with plush materials and adorned with pastel colors, these towels are designed to make every post-shower a delightful experience.',
        'Conoce las toallas Marshmallow: irresistiblemente suaves, con relieve acolchonado y colores preciosos que hacen especial cada baño. Fáciles de usar y de cuidar todos los días.',
        'Meet Marshmallow: irresistibly soft towels with sculpted texture, beautiful colours and everyday comfort.']
    ],
    'pages/pure-cotton-collection': [
      ['The luxury of 100% cotton, reimagined with a softer handfeel, plush texture, and elevated finish.',
        'El encanto del algodón 100% en una toalla con textura esponjosa y acabado de lujo.',
        'The luxury of 100% cotton with a plush, beautifully textured finish.'],
      ['Style Your Space', 'Dale estilo a tu baño', 'Style Your Space'],
      ['The perfect weight for everyday use.', 'La textura y el peso que amas, para todos los días.', 'A plush, comforting weight for everyday use.'],
      ['100% Long-Staple Cotton', 'Algodón de fibra larga 100%', '100% Long-Staple Cotton']
    ],
    'pages/the-chevron-towel': [
      ['DISCOVER CHEVRON', 'DESCUBRE CHEVRON', 'DISCOVER CHEVRON'],
      ['The Chevron Towel does it all.', 'Diseño que enamora. Suavidad que se siente.', 'Beautiful texture. Everyday comfort.'],
      ['Transform your daily routine with the iconic Chevron Towel. Combining style and sophistication, these towels boast chic pastel hues that elevate any bathroom decor. Crafted from premium materials, the Chevron Towel is highly absorbent and luxuriously soft, ensuring a spa-like experience every time you step out of the shower. Indulge in the unparalleled quality and comfort that makes the Chevron Towel a must-have addition to your home.',
        'Dale un toque especial a tu baño con el relieve Chevron y sus tonos elegantes. Una toalla que combina absorción, suavidad y mucho estilo para el día a día.',
        'Elevate your bathroom with sculpted Chevron patterns, lovely colours and everyday comfort.']
    ],
    'collections/all-1': [
      ['All Products', 'Descubre Mallo México', 'Explore Mallo Mexico'],
      ['Up to 40% Off Sitewide', 'Encuentra tu nueva toalla favorita', 'Discover your new favourite towels'],
      ['The Marshmallow Series you love, newly improved for even more softness and plush comfort.',
        'La suavidad Marshmallow que te encanta, ahora con una textura todavía más esponjosa.',
        'Meet our newly improved, cloud-soft Marshmallow towels.'],
      ['Limited-edition Marshmallow Towel releases, made in special colors for a short time only.',
        'Tus toallas Marshmallow favoritas en colores de edición limitada.',
        'Your favourite Marshmallow softness in limited-edition colours.']
    ],
    'collections/all': [['All Products', 'Descubre Mallo México', 'Explore Mallo Mexico']],
    'pages/about-us': [
      ['Towels that feel good, every day.', 'Toallas que se sienten increíbles, todos los días.', 'Towels that feel wonderful, every day.']
    ],
    'pages/contact': [
      ["Whether you have a question about our products, need assistance with an order, or just want to say hello, we're here and ready to help. Your satisfaction is our top priority, and we're dedicated to providing you with exceptional customer service.",
        '¿Tienes preguntas sobre nuestras toallas, envíos a México o tu pedido? Escríbenos. Nos encantará ayudarte.',
        'Questions about our towels, delivery to Mexico or your order? Our team is happy to help.']
    ],
    'pages/partners': [
      ['Softness, made for sharing.', 'Suavidad para compartir.', 'Softness, made for sharing.'],
      ["Let's make something soft together.", 'Hagamos algo especial juntos.', 'Let’s create something memorable.']
    ],
    'blogs/mallo-living': [
      ['Thoughtful guides on towels, fabric care, bathroom rituals, softness, wellness, and the small details that make everyday living feel more comfortable.',
        'Consejos para cuidar tus toallas, ideas para tu baño y pequeños rituales que hacen más cómoda la vida diaria.',
        'Towel-care tips, bathroom inspiration and ideas for everyday comfort.']
    ],
    'pages/faqs': [
      ['Are your towels absorbent?', '¿Sus toallas absorben bien?', 'Are your towels absorbent?'],
      ['Yes! They are highly absorbent.', '¡Sí! Están diseñadas para absorber muy bien la humedad.', 'Yes, they are designed to absorb moisture effectively.'],
      ['Are your towels machine washable?', '¿Puedo lavarlas en lavadora?', 'Are your towels machine washable?'],
      ['Yes! They can be machine washed.', 'Sí. Sigue las indicaciones de cuidado de cada producto.', 'Yes. Please follow the product care label.'],
      ['Can your towels be tumble dried?', '¿Puedo secarlas en secadora?', 'Can your towels be tumble dried?'],
      ['Yes! They can be tumble dried.', 'Consulta la etiqueta de cuidados de tu toalla.', 'Please check your product care label.']
    ],
    'pages/the-marshmallow-towel-a-gift-for-her': [
      ["Don't know what to buy her?", '¿No sabes qué regalarle?', 'Not sure what to get her?'],
      ["WE'VE GOT YOUR BACK.", 'TENEMOS EL REGALO PERFECTO.', 'WE HAVE JUST THE THING.'],
      ['Make her day special.', 'Haz que su día sea especial.', 'Make her day special.'],
      ['GIVE HER A GIFT', 'UN REGALO QUE', 'A GIFT SHE WILL'],
      ['SHE WILL USE & CHERISH', 'VA A USAR Y AMAR', 'USE & LOVE']
    ],
    'pages/about-the-marshmallow-towel': [
      ['About the Marshmallow Towel', 'Conoce la toalla Marshmallow', 'About the Marshmallow Towel']
    ],
    'pages/home': [
      ['The Marshmallow Towel', 'La toalla Marshmallow', 'The Marshmallow Towel']
    ]
  };

  /* Informational merchandising panels: Spanish and Mexico English. */
  const MX_PANELS = {
    home: {
      es: ['¡HOLA, MÉXICO! 🇲🇽', 'Las toallas más bonitas y suavecitas llegaron a tu rutina.',
        'Imagina salir del baño y envolverte en la textura esponjosa de Marshmallow. Colores que enamoran, suavidad que se siente y una sensación ligera todos los días.',
        ['Suaves como nube', 'Ligeras y de secado rápido', 'Colores para lucir tu baño'], 'Descubre Marshmallow', 'pages/the-marshmallow-towel'],
      en: ['HELLO, MEXICO! 🇲🇽', 'Your next favourite towels are here.',
        'Imagine finishing your shower wrapped in signature Marshmallow ridges. Beautiful colours, an irresistibly plush feel and everyday lightweight comfort.',
        ['Cloud-soft feel', 'Lightweight and quick-drying', 'Beautiful bathroom colours'], 'Explore Marshmallow', 'pages/the-marshmallow-towel']
    },
    'pages/the-marshmallow-towel': {
      es: ['MALLO MÉXICO', 'Tu momento más suave empieza aquí.',
        'Marshmallow 2.0 combina una textura acolchonada que dan ganas de abrazar con una sensación ligera y práctica para todos los días.',
        ['Textura irresistiblemente suave', 'Ligera y absorbente', 'Fácil de lavar y cuidar'], 'Ver todas las toallas', 'collections/all-1'],
      en: ['THE MEXICO EDIT', 'Make every shower a little softer.',
        'Marshmallow 2.0 combines signature cloud-soft ridges with a lightweight, practical feel for everyday use.',
        ['Irresistibly plush texture', 'Lightweight and absorbent', 'Easy everyday care'], 'Browse all towels', 'collections/all-1']
    },
    'pages/pure-cotton-collection': {
      es: ['ALGODÓN 100%', 'Esa sensación de hotel, ahora en casa.',
        'Para quienes prefieren el tacto clásico del algodón: fibras largas, textura esponjosa y estructura CloudLoom™ de 430 GSM.',
        ['Algodón de fibra larga 100%', '430 GSM', 'Textura suave y con cuerpo']],
      en: ['100% LONG-STAPLE COTTON', 'A hotel-inspired feeling at home.',
        'For cotton lovers: long-staple fibres, plush texture and 430 GSM CloudLoom™ construction.',
        ['100% long-staple cotton', '430 GSM', 'Comfortably plush texture']]
    },
    'pages/the-chevron-towel': {
      es: ['TEXTURA CON ESTILO', 'Renueva tu baño con un toque de diseño.',
        'La textura Chevron y sus tonos elegantes combinan comodidad y estilo en cada uso.',
        ['Diseño Chevron con relieve', 'Suave al tacto', 'Para todos los días']],
      en: ['TEXTURE MEETS STYLE', 'A little more beauty for your bathroom.',
        'Sculpted Chevron patterns and lovely tones bring comfort and character to your space.',
        ['Sculpted Chevron design', 'Soft to the touch', 'Everyday comfort']]
    },
    'collections/all-1': {
      es: ['BIENVENIDO A MALLO MÉXICO', 'Encuentra tu tipo de suavidad.',
        'Elige entre las toallas ligeras Marshmallow, la textura de algodón puro, Chevron y nuestros colores especiales. Arma tu paquete ideal.',
        ['Signature Plush', 'Algodón puro', 'Chevron y ediciones limitadas']],
      en: ['WELCOME TO MALLO MEXICO', 'Find your kind of soft.',
        'Explore Marshmallow 2.0, Pure Cotton, Chevron and limited-release colours. Discover a towel set for every routine.',
        ['Signature Plush', 'Pure Cotton', 'Chevron & limited colours']]
    },
    'collections/all': {
      es: ['MALLO MÉXICO', 'Encuentra tu nueva toalla favorita.', 'Toallas suaves, ligeras y con colores que le dan vida a tu baño.'],
      en: ['MALLO MEXICO', 'Find your next favourite towel.', 'Cloud-soft textures and beautiful colours for everyday living.']
    },
    'pages/about-us': {
      es: ['HOLA DESDE MALLO', 'Hacemos especiales los pequeños momentos.',
        'Creemos que una buena toalla debe sentirse increíble, verse bonita y hacer que tu rutina sea un poquito mejor. ¡Bienvenido a Mallo!'],
      en: ['HELLO FROM MALLO', 'Making little everyday moments feel special.',
        'We believe a lovely towel should feel wonderful, look beautiful and make your everyday routine a little brighter.']
    },
    'pages/faqs': {
      es: ['¿COMPRAS DESDE MÉXICO?', 'Lo que debes saber antes de pedir.',
        'El envío, las formas de pago y los posibles cargos de importación dependen de tu pedido y dirección. Consulta los detalles al finalizar la compra.'],
      en: ['SHOPPING FROM MEXICO?', 'A few things to know before ordering.',
        'Delivery options, available payments and possible import costs depend on your order and address. Check the details at checkout.']
    },
    'pages/contact': {
      es: ['¿PREGUNTAS DESDE MÉXICO?', '¡Con gusto te ayudamos!',
        '¿Tienes dudas sobre tu pedido, materiales, cuidados o envíos? Escríbenos e incluye tu estado y código postal si tu pregunta es sobre entregas.',
        null, 'Escribir a soporte', null, 'mailto:support@mallotowels.com'],
      en: ['QUESTIONS FROM MEXICO?', 'We are happy to help.',
        'For questions about products, orders or delivery, contact our team. Include your state and postal code for shipping queries.',
        null, 'Email Mallo Support', null, 'mailto:support@mallotowels.com']
    },
    'pages/partners': {
      es: ['ALIANZAS EN MÉXICO', 'El detalle perfecto para regalar y recibir.',
        '¿Buscas regalos corporativos, toallas para hoteles, spas, eventos o tiendas en México? Cuéntanos sobre tu proyecto y las cantidades aproximadas.',
        ['Regalos empresariales', 'Hoteles, resorts y spas', 'Eventos y tiendas']],
      en: ['MEXICO PARTNERSHIPS', 'Thoughtful gifting and everyday comfort.',
        'Planning corporate gifts, hospitality, spas, events or retail in Mexico? Tell us about your project and estimated quantities.',
        ['Corporate gifts', 'Hotels, resorts & spas', 'Events & retail']]
    },
    'collections/limited-edition-releases': {
      es: ['COLORES DE EDICIÓN LIMITADA', 'Tus favoritos no estarán para siempre.',
        'Descubre nuestros tonos especiales con la misma suavidad Marshmallow. Elige el tuyo mientras esté disponible.'],
      en: ['LIMITED-RELEASE COLOURS', 'Your favourites may not last forever.',
        'Discover our signature Marshmallow texture in special-release shades.']
    },
    'collections/bath-essentials': {
      es: ['ESENCIALES DE BAÑO', 'Pequeños detalles para consentirte.',
        'Descubre accesorios para convertir tu rutina en un momento especial. La disponibilidad se confirma al finalizar tu compra.'],
      en: ['BATH ESSENTIALS', 'Small comforts, every day.',
        'Explore bathroom essentials that make everyday routines special. Availability depends on your checkout location.']
    },
    'blogs/mallo-living': {
      es: ['EL BLOG DE MALLO', 'Ideas para una rutina más suave.',
        'Consejos para cuidar tus toallas, inspirarte con tu baño y disfrutar de los pequeños momentos del día.'],
      en: ['THE MALLO JOURNAL', 'Small details make everyday living softer.',
        'Towel-care tips, bathroom inspiration and little ways to find everyday comfort.']
    },
    'policies/shipping-policy': {
      es: ['ENVÍOS A MÉXICO', 'Consulta los detalles de entrega antes de pagar.',
        'El costo, servicio y tiempo de entrega dependen de tu pedido y dirección. Podrían aplicarse impuestos, aranceles o cargos aduanales. Si necesitas confirmar quién los cubre, contáctanos antes de comprar.',
        null, 'Preguntar sobre envíos', 'pages/contact'],
      en: ['SHIPPING TO MEXICO', 'Please check delivery terms at checkout.',
        'Shipping charges, delivery services and timing depend on your order and address. Import taxes, duties or clearance fees may apply; contact support to confirm who pays them.',
        null, 'Contact support', 'pages/contact']
    },
    'policies/refund-policy': {
      es: ['DEVOLUCIONES DESDE MÉXICO', 'Contáctanos antes de hacer una devolución.',
        'Se aplican las condiciones de la política de devoluciones publicada, incluidas las restricciones de productos en oferta. La devolución internacional podría generar gastos de envío.',
        null, 'Contactar a soporte', 'pages/contact'],
      en: ['RETURNS FROM MEXICO', 'Please contact us before sending an item back.',
        'The published Returns Policy and its sale-item eligibility restrictions apply. International return shipping may incur costs.',
        null, 'Contact support', 'pages/contact']
    },
    cart: {
      es: ['PEDIDOS DESDE MÉXICO', 'Revisa las opciones de envío y pago.',
        'La moneda se configura automáticamente en la tienda. Verifica el total final, el envío y las formas de pago disponibles al finalizar tu compra.'],
      en: ['MEXICO CHECKOUT', 'Review delivery and payment options.',
        'Currency is handled by the store automatically. Check the final order total, shipping and available payment methods at checkout.']
    },
    'pages/the-marshmallow-towel-a-gift-for-her': {
      es: ['REGALOS CON SUAVIDAD', 'Regálale un momento especial.',
        'Sorpréndela con la suavidad Marshmallow y los colores que harán más bonita su rutina.'],
      en: ['THOUGHTFUL GIFTING', 'Give her a little everyday luxury.',
        'Surprise her with cloud-soft Marshmallow texture and beautiful shades.']
    },
    'pages/about-the-marshmallow-towel': {
      es: ['ASÍ SE SIENTE MARSHMALLOW', 'Tu nuevo básico favorito del baño.',
        'Fibras suaves, textura con relieve y colores preciosos. Conoce nuestras toallas y encuentra tu favorita.'],
      en: ['ABOUT MARSHMALLOW', 'Your new favourite everyday bathroom essential.',
        'Soft fibres, sculpted ridges and beautiful shades.']
    }
  };
  const MX_COLLECTION_FALLBACK = {
    es: ['EXPLORA MALLO', 'Toallas para cada estilo.', 'Encuentra tu textura, color y tamaño favoritos para disfrutar cada baño.'],
    en: ['EXPLORE MALLO', 'Find your favourite towels.', 'Discover the right texture, colour and size for your routine.']
  };
  const MX_FAQS = {
    es: [
      ['¿Puedo comprar Mallo desde México?', 'Selecciona México como país de entrega, ingresa tu dirección y revisa las opciones disponibles al finalizar tu compra.'],
      ['¿Cuánto cuesta el envío a México?', 'El costo y el servicio de envío dependen de tu pedido y código postal. El total final se muestra antes de pagar.'],
      ['¿En cuánto tiempo recibiré mi pedido?', 'Los tiempos de entrega dependen de la ubicación, el transportista y el procesamiento del pedido. Contáctanos si necesitas una estimación más precisa.'],
      ['¿Tendré que pagar impuestos o cargos aduanales?', 'En envíos internacionales podrían aplicarse impuestos, aranceles y gastos de despacho. Para saber cómo se manejan en tu pedido, consulta con nuestro equipo antes de comprar.'],
      ['¿Puedo pagar con tarjeta o con métodos de pago de México?', 'Las formas de pago disponibles se muestran al finalizar tu compra. No garantizamos un método específico a menos que aparezca en el checkout.'],
      ['¿Cuál es la diferencia entre Signature Plush y Pure Cotton?', 'Signature Plush es una microfibra de 85% poliéster y 15% nailon: suave, ligera y fácil de cuidar. Pure Cotton es algodón de fibra larga 100%, de 430 GSM, con una textura más densa tipo hotel.'],
      ['¿Cómo funcionan las devoluciones desde México?', 'Se aplican las condiciones y exclusiones de la política de devoluciones publicada, incluidas las de artículos en oferta. Contacta a soporte antes de realizar un envío de devolución internacional.']
    ],
    en: [
      ['Can I order from Mexico?', 'Select Mexico as your delivery destination, enter your address and review the options offered at checkout.'],
      ['What does shipping to Mexico cost?', 'Delivery options and charges depend on your order and postal code. Check the final amount at checkout.'],
      ['How long does delivery take?', 'Timing depends on the destination, courier and processing arrangements. Contact us if you need a closer estimate.'],
      ['Are customs duties or import taxes charged?', 'International parcels may incur customs duties, import taxes or clearance fees. Contact our team if you need to confirm how those are handled.'],
      ['Which payment methods can I use?', 'The payment methods available to your order appear at checkout. Specific local payment methods are not guaranteed.'],
      ['What is the difference between Signature Plush and Pure Cotton?', 'Signature Plush is an 85% polyester / 15% nylon microfibre blend for lightweight cloud-soft comfort. Pure Cotton uses 100% long-staple cotton at 430 GSM for a fuller, hotel-inspired feel.'],
      ['Can I return an order from Mexico?', 'The published Returns Policy and its exclusions, including restrictions on sale items, apply. Contact support before returning an international order.']
    ]
  };

  function mexicanTextTransform(value, mode, region) {
    const current = normalized(value);
    if (!current) return value;
    const match = (MX_PAGE_PAIRS[pageKey] || []).find(pair => normalized(pair[0]) === current);
    if (match) return match[mode === 'es' ? 1 : 2];
    if (mode === 'es' && Object.prototype.hasOwnProperty.call(MX_COMMON, current)) return MX_COMMON[current];
    // The original USD threshold must not be repurposed for Mexico without
    // specific confirmation; replace only known marketing text, not totals.
    if (region === 'chrome') {
      let changed = value;
      for (const [oldText, newText] of Object.entries(MX_BANNER[mode])) {
        if (changed.includes(oldText)) changed = changed.split(oldText).join(newText);
      }
      return changed;
    }
    if (current === 'Free shipping on orders over $99.') {
      return mode === 'es' ? 'Consulta el costo de envío al finalizar tu compra.' : 'Shipping options are shown at checkout.';
    }
    return value;
  }

  function translateMexicoTree(root, mode, region) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    for (const textNode of nodes) {
      if (shouldSkip(textNode.parentElement)) continue;
      // Leave legal terms, reviews, monetary calculations, form options and
      // the Shopify cart body in the merchant's authoritative original text.
      if (region === 'main' && (pageKey.startsWith('policies/') || pageKey === 'cart')) continue;
      if (textNode.parentElement.closest && textNode.parentElement.closest(
        '[data-mallo-mx],.jdgm-rev,.loox-review,.okeReviews,.spr-review,blockquote,' +
        '[data-review-id],.product-price,.cart-totals,[data-money],.product-form__buttons')) continue;
      const previous = MX_ORIGINALS.get(textNode);
      const original = previous && previous.rendered === textNode.nodeValue ? previous.original : textNode.nodeValue;
      const translated = mexicanTextTransform(original, mode, region);
      MX_ORIGINALS.set(textNode, { original, rendered: translated });
      if (translated !== textNode.nodeValue) textNode.nodeValue = translated;
    }
  }

  function injectMexicoStyles() {
    if (document.getElementById('mallo-mx-v5-css')) return;
    const style = document.createElement('style');
    style.id = 'mallo-mx-v5-css';
    style.textContent = `
      [data-mallo-mx] { box-sizing:border-box; }
      .mallo-mx-panel { box-sizing:border-box; width:100%; background:linear-gradient(118deg,#fff9fa,#fbeceb 55%,#f4effa); color:#203151; padding:clamp(25px,4vw,54px) 20px; }
      .mallo-mx-inner { max-width:1160px; margin:auto; display:flex; gap:28px; justify-content:space-between; align-items:center; }
      .mallo-mx-copy { max-width:800px; min-width:0; }
      .mallo-mx-eyebrow { display:block; color:#72629d; letter-spacing:.09em; font-size:11px; font-weight:700; margin:0 0 12px; }
      .mallo-mx-heading { color:#203151; font-size:clamp(23px,2.55vw,37px); line-height:1.25; font-weight:650; letter-spacing:-.02em; margin:0 0 12px; }
      .mallo-mx-body { color:#3e4e6e; font-size:clamp(14px,1.13vw,16px); line-height:1.8; margin:0; }
      .mallo-mx-benefits { display:flex; flex-wrap:wrap; gap:8px; list-style:none; padding:0; margin:18px 0 0; }
      .mallo-mx-benefits li { font-size:12px; font-weight:600; padding:7px 13px; border:1px solid #ded6eb; background:#ffffffb8; border-radius:30px; }
      .mallo-mx-cta { display:inline-flex; align-items:center; justify-content:center; text-align:center; min-height:44px; padding:12px 20px; border-radius:80px; background:#203151; color:#fff!important; text-decoration:none!important; font-size:13px; font-weight:700; flex-shrink:0; }
      .mallo-mx-panel--simple { padding:22px 20px; background:#faf8fd; border-block:1px solid #e9e3f1; }
      .mallo-mx-panel--simple .mallo-mx-heading { font-size:clamp(20px,2vw,27px); }
      .mallo-mx-questions { max-width:980px; margin:30px auto; padding:0 20px; color:#203151; }
      .mallo-mx-questions h2 { color:#203151; font-size:clamp(20px,2.3vw,30px); margin:0 0 18px; }
      .mallo-mx-questions details { border-bottom:1px solid #e8e5ee; padding:15px 0; }
      .mallo-mx-questions summary { cursor:pointer; color:#203151; font-size:15px; font-weight:650; }
      .mallo-mx-questions p { color:#3e4e6e; font-size:14px; line-height:1.75; margin:10px 0 0; }
      .mallo-mx-product { background:#fbf7fb; color:#203151; border:1px solid #e8dff0; border-radius:14px; margin:18px 0; padding:16px 18px; max-width:720px; }
      .mallo-mx-product strong { display:block; margin-bottom:7px; font-size:13px; }
      .mallo-mx-product ul { padding:0; margin:0; list-style:none; display:flex; gap:8px 20px; flex-wrap:wrap; }
      .mallo-mx-product li { font-size:12px; line-height:1.5; }
      .mallo-mx-product li::before { content:'✓'; margin-right:7px; color:#72629d; font-weight:700; }
      .mallo-mx-toggle { position:fixed; right:max(14px,env(safe-area-inset-right)); bottom:calc(18px + env(safe-area-inset-bottom,0px)); z-index:55; display:inline-flex; gap:3px; padding:4px; max-width:calc(100vw - 24px); background:#fff; border:1px solid #dbdce8; border-radius:99px; box-shadow:0 5px 24px #20315124; font-family:inherit; }
      .mallo-mx-toggle button { border:0; border-radius:40px; background:transparent; padding:9px 13px; min-width:71px; cursor:pointer; font-size:12px; font-weight:700; color:#203151; line-height:1.2; }
      .mallo-mx-toggle button[aria-pressed="true"] { background:#203151; color:#fff; }
      .mallo-mx-toggle button:focus-visible { outline:2px solid #72629d; outline-offset:3px; }
      @media(max-width:749px) { .mallo-mx-inner { display:block; } .mallo-mx-panel { padding:28px 18px; } .mallo-mx-panel--simple { padding:19px 16px; } .mallo-mx-cta { margin-top:17px; white-space:normal; } .mallo-mx-toggle { right:10px; bottom:calc(14px + env(safe-area-inset-bottom,0px)); } }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  function populateMexicoPanel(panel, info) {
    panel.textContent = '';
    panel.setAttribute('data-mallo-language', mexicoLanguage);
    panel.setAttribute('lang', mexicoLanguage === 'es' ? 'es-MX' : 'en');
    const inner = el('div', 'mallo-mx-inner');
    const copy = el('div', 'mallo-mx-copy');
    if (info[0]) copy.appendChild(el('span', 'mallo-mx-eyebrow', info[0]));
    if (info[1]) copy.appendChild(el('h2', 'mallo-mx-heading', info[1]));
    if (info[2]) copy.appendChild(el('p', 'mallo-mx-body', info[2]));
    if (Array.isArray(info[3]) && info[3].length) {
      const list = el('ul', 'mallo-mx-benefits');
      info[3].forEach(item => list.appendChild(el('li', '', item)));
      copy.appendChild(list);
    }
    inner.appendChild(copy);
    if (info[4]) {
      const a = el('a', 'mallo-mx-cta', info[4]);
      a.href = info[6] || href(info[5]);
      inner.appendChild(a);
    }
    panel.appendChild(inner);
  }
  function mountMexicoPanel(main) {
    const entries = MX_PANELS[pageKey] || (pageKey.startsWith('collections/') ? MX_COLLECTION_FALLBACK : null);
    if (!main || !entries) return;
    const simple = pageKey.startsWith('policies/') || pageKey === 'cart';
    let panel = main.querySelector('[data-mallo-mx="page-panel"]');
    if (!panel) {
      const heading = main.querySelector('h1');
      const section = heading && heading.closest('.shopify-section');
      const host = section && section.parentElement === main ? section : main.firstElementChild;
      if (!host) return;
      panel = el('section', 'mallo-mx-panel' + (simple ? ' mallo-mx-panel--simple' : ''));
      panel.setAttribute('data-mallo-mx', 'page-panel');
      panel.setAttribute('aria-label', 'Información de Mallo México');
      if (simple) host.before(panel); else host.after(panel);
    }
    if (panel.getAttribute('data-mallo-language') !== mexicoLanguage) populateMexicoPanel(panel, entries[mexicoLanguage]);
  }
  function mountMexicoFAQs(main) {
    if (!main || pageKey !== 'pages/faqs') return;
    let container = main.querySelector('[data-mallo-mx="faq-questions"]');
    if (!container) {
      container = el('section', 'mallo-mx-questions');
      container.setAttribute('data-mallo-mx', 'faq-questions');
      main.appendChild(container);
    }
    if (container.getAttribute('data-mallo-language') === mexicoLanguage) return;
    container.textContent = '';
    container.setAttribute('data-mallo-language', mexicoLanguage);
    container.setAttribute('lang', mexicoLanguage === 'es' ? 'es-MX' : 'en');
    container.appendChild(el('h2', '', mexicoLanguage === 'es' ? 'Compras desde México: preguntas frecuentes' : 'Shopping from Mexico — FAQs'));
    for (const [question, answer] of MX_FAQS[mexicoLanguage]) {
      const item = el('details');
      item.appendChild(el('summary', '', question));
      item.appendChild(el('p', '', answer));
      container.appendChild(item);
    }
    const cta = el('a', 'mallo-mx-cta', mexicoLanguage === 'es' ? 'Escríbenos' : 'Contact support');
    cta.href = href('pages/contact');
    container.appendChild(cta);
  }
  function mountMexicoBenefits(main) {
    if (!main || !pageKey.startsWith('products/')) return;
    const heading = main.querySelector('h1');
    if (!heading) return;
    const title = normalized(heading.textContent + ' ' + pageKey).toLowerCase();
    if (!/towel|marshmallow|chevron/.test(title) || /soap|pumpkin|bomb/.test(title)) return;
    const isCotton = /cotton|algodón/.test(title) && !/2\.0/.test(title);
    const isChevron = /chevron/.test(title);
    let card = main.querySelector('[data-mallo-mx="product-benefits"]');
    if (!card) {
      card = el('aside', 'mallo-mx-product');
      card.setAttribute('data-mallo-mx', 'product-benefits');
      const info = heading.closest('.product-information__content,.product-details,.product__info-wrapper,.product-info,.product-information');
      if (info && !info.closest('form')) info.appendChild(card);
      else {
        const section = heading.closest('.shopify-section');
        if (section && section.parentElement === main) section.after(card);
        else main.appendChild(card);
      }
    }
    if (card.getAttribute('data-mallo-language') === mexicoLanguage) return;
    card.textContent = '';
    card.setAttribute('lang', mexicoLanguage === 'es' ? 'es-MX' : 'en');
    card.setAttribute('data-mallo-language', mexicoLanguage);
    const features = isCotton ? {
      es: ['Algodón de fibra larga 100%', 'Textura suave de 430 GSM', 'Estructura CloudLoom™'],
      en: ['100% long-staple cotton', 'Plush 430 GSM feel', 'CloudLoom™ construction']
    } : isChevron ? {
      es: ['Diseño Chevron con relieve', 'Suaves y absorbentes', 'Para disfrutar todos los días'],
      en: ['Sculpted Chevron design', 'Soft and absorbent', 'Made for everyday use']
    } : {
      es: ['Relieves suaves como nube', 'Ligeras y de secado rápido', 'Fáciles de lavar y cuidar'],
      en: ['Cloud-soft Marshmallow ridges', 'Lightweight and quick-drying', 'Easy everyday care']
    };
    card.appendChild(el('strong', '', mexicoLanguage === 'es' ? 'Dale un toque de suavidad a tus días' : 'A little more softness for everyday life'));
    const ul = el('ul');
    features[mexicoLanguage].forEach(point => ul.appendChild(el('li', '', point)));
    card.appendChild(ul);
  }
  function mountMexicoToggle() {
    let toggle = document.querySelector('[data-mallo-mx="language-toggle"]');
    if (!toggle) {
      toggle = el('div', 'mallo-mx-toggle');
      toggle.setAttribute('data-mallo-mx', 'language-toggle');
      toggle.setAttribute('role', 'group');
      toggle.setAttribute('aria-label', 'Idioma del sitio en México / Mexico website language');
      for (const [lang, label] of [['es', 'Español'], ['en', 'English']]) {
        const button = el('button', '', label);
        button.type = 'button';
        button.setAttribute('data-mallo-mx-lang', lang);
        button.addEventListener('click', () => setMexicoLanguage(lang));
        toggle.appendChild(button);
      }
      document.body.appendChild(toggle);
    }
    for (const button of toggle.querySelectorAll('button')) {
      button.setAttribute('aria-pressed', button.getAttribute('data-mallo-mx-lang') === mexicoLanguage ? 'true' : 'false');
    }
  }
  function setMexicoLanguage(lang) {
    if (!MX || (lang !== 'es' && lang !== 'en')) return;
    mexicoLanguage = lang;
    saveMexicoLanguage(lang);
    update();
  }
  function localiseMexico() {
    injectMexicoStyles();
    const main = mainElement();
    if (main) {
      mountMexicoBenefits(main);
      translateMexicoTree(main, mexicoLanguage, 'main');
      mountMexicoPanel(main);
      mountMexicoFAQs(main);
    }
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    for (const item of [header, footer]) translateMexicoTree(item, mexicoLanguage, 'chrome');
    for (const item of document.querySelectorAll('[class*="announcement-bar"],[class*="announcement_bar"],[id*="announcement-bar"]')) {
      if (!header || !header.contains(item)) translateMexicoTree(item, mexicoLanguage, 'chrome');
    }
    mountMexicoToggle();
  }

  /* ====================== UNITED KINGDOM — v6 ======================
   * Shopify Markets ISO country is GB (not UK).
   * Editorial copy in British English; no language toggle is necessary.
   * Automatic currency display, shipping rates, discounts, tax, forms,
   * product data and checkout are not changed.
   */
  const UK_ORIGINALS = new WeakMap();
  const UK_MAIN_COPY = {
    home: [
      ['Introducing the new', 'HELLO, UK! 🇬🇧'],
      ['Turn every shower into a spa-like escape with plush texture and lasting softness.',
        'Your everyday shower deserves a little luxury. Discover cloud-soft, beautifully textured towels that make getting out of the bath feel just as good.'],
      ['Five new colorways. While stocks last.', 'Five new colourways. Here for a limited time.'],
      ["The same cloud-soft Marshmallow ridge, in a run of colors that won't stick around — grab your favorite before it's gone.",
        'Your favourite Marshmallow texture, now in a collection of striking new colours. Find your perfect match while they last.'],
      ['More than just marshmallows', 'A softer way to do everyday'],
      ["Chevron towels, bath bombs, soap, and the softest little pumpkins you'll ever squeeze — the wider Mallo world, curated.",
        'From sculpted Chevron towels to lovely little bathroom extras, find the finishing touches to your daily routine.'],
      ['Fast lightweight absorbency', 'Lightweight, absorbent comfort'],
      ['From the journal', 'The Mallo Journal']
    ],
    'pages/the-marshmallow-towel': [
      ['THICKER, SOFTER, AND FLUFFIER THAN EVER', 'SOFTER DAYS START WITH MARSHMALLOW'],
      ['the ordinary shower towel reimagined', 'the everyday bath towel, beautifully reimagined'],
      ['REDEFINE YOUR SHOWER EXPERIENCE', 'TURN AN EVERYDAY SHOWER INTO A LITTLE LUXURY'],
      ['Experience the ultimate comfort and cuteness with our Marshmallow Towel collection. Crafted with plush materials and adorned with pastel colors, these towels are designed to make every post-shower a delightful experience.',
        'Meet the towels that make an ordinary shower feel special. Velvety Marshmallow ridges, beautiful colours and a wonderfully soft, lightweight feel — perfect for your everyday routine.']
    ],
    'pages/pure-cotton-collection': [
      ['The luxury of 100% cotton, reimagined with a softer handfeel, plush texture, and elevated finish.',
        'Meet the indulgent side of everyday cotton. Made from 100% long-staple cotton, with a beautifully plush, hotel-inspired feel.'],
      ['Style Your Space', 'Bring a Boutique Hotel Feeling Home'],
      ['The perfect weight for everyday use.', 'Soft, substantial comfort at 430 GSM.']
    ],
    'pages/the-chevron-towel': [
      ['DISCOVER CHEVRON', 'MEET YOUR NEW BATHROOM FAVOURITE'],
      ['The Chevron Towel does it all.', 'Beautiful texture, brilliant everyday comfort.'],
      ['Transform your daily routine with the iconic Chevron Towel. Combining style and sophistication, these towels boast chic pastel hues that elevate any bathroom decor. Crafted from premium materials, the Chevron Towel is highly absorbent and luxuriously soft, ensuring a spa-like experience every time you step out of the shower. Indulge in the unparalleled quality and comfort that makes the Chevron Towel a must-have addition to your home.',
        'Refresh your bathroom with sculpted Chevron texture, lovely colours and a soft, comfortable feel. A small everyday upgrade that looks as good as it feels.']
    ],
    'collections/all-1': [
      ['All Products', 'Shop Mallo UK'],
      ['The Marshmallow Series you love, newly improved for even more softness and plush comfort.',
        'Our much-loved Marshmallow texture, now even softer and more plush. Find the towel that makes your everyday routine feel special.'],
      ['Limited-edition Marshmallow Towel releases, made in special colors for a short time only.',
        'Our signature Marshmallow towels in limited-edition colours. Pick your favourite while it is here.']
    ],
    'collections/all': [
      ['All Products', 'Shop Mallo UK']
    ],
    'pages/about-us': [
      ['Towels that feel good, every day.', 'A little everyday luxury, from Mallo to the UK.']
    ],
    'pages/contact': [
      ["Whether you have a question about our products, need assistance with an order, or just want to say hello, we're here and ready to help. Your satisfaction is our top priority, and we're dedicated to providing you with exceptional customer service.",
        'Have a question about our towels, delivery to the UK or an existing order? Our team is happy to help.']
    ],
    'pages/partners': [
      ['Softness, made for sharing.', 'Thoughtful gifting, made softer.'],
      ["Let's make something soft together.", 'Let’s make something memorable together.']
    ],
    'blogs/mallo-living': [
      ['Thoughtful guides on towels, fabric care, bathroom rituals, softness, wellness, and the small details that make everyday living feel more comfortable.',
        'Practical tips on washing, drying and caring for your towels, plus bathroom inspiration for a softer everyday routine.']
    ]
  };

  const UK_CHROME_PAIRS = [
    ['AUTUMN IS HERE 🍂', 'HELLO, UK! 🇬🇧'],
    // Leave the live AUTUMN25 offer unchanged; eligibility is controlled by Shopify.
    ['Free tracked shipping over $99', 'UK delivery options at checkout'],
    ['Free shipping on orders over $99.', 'See UK delivery options at checkout.'],
    ['Free Shipping on qualifying orders', 'Delivery options shown at checkout'],
    ['Free Shipping On qualifying orders', 'Delivery options shown at checkout']
  ];
  const UK_MAIN_NEUTRAL_SHIPPING = [
    ['Free shipping on orders over $99.', 'See UK delivery options at checkout.'],
    ['Free tracked shipping over $99', 'UK delivery options at checkout'],
    ['Free Shipping on qualifying orders', 'Delivery options shown at checkout'],
    ['Free Shipping On qualifying orders', 'Delivery options shown at checkout']
  ];

  const UK_PANELS = {
    home: {
      eyebrow: 'HELLO, UNITED KINGDOM',
      title: 'Your everyday shower. A whole lot softer.',
      body: 'Picture your favourite bit of a cosy Sunday, any day of the week. Meet our beautifully textured Marshmallow towels: cloud-soft against your skin, lovely on the towel rail and lightweight for everyday use.',
      points: ['Wonderfully soft', 'Beautiful bathroom colours', 'Lightweight, easy-care design'],
      cta: 'Explore Marshmallow 2.0', path: 'pages/the-marshmallow-towel'
    },
    'pages/the-marshmallow-towel': {
      eyebrow: 'MEET YOUR NEW EVERYDAY FAVOURITE',
      title: 'A little luxury after every shower.',
      body: 'Our Signature Plush blend brings velvety Marshmallow ridges and quick-drying, lightweight comfort to your daily routine. Beautifully soft, without the bulk of a traditional thick towel.',
      points: ['Cloud-soft ridge texture', 'Lightweight feel', 'Easy everyday care'],
      cta: 'Explore all towels', path: 'collections/all-1'
    },
    'pages/pure-cotton-collection': {
      eyebrow: 'FOR THE COTTON LOVERS',
      title: 'That boutique hotel feeling, at home.',
      body: 'If you love the familiar comfort of cotton, meet our 100% long-staple Pure Cotton range. Made with CloudLoom™ construction at a plush 430 GSM.',
      points: ['100% long-staple cotton', '430 GSM', 'Rich, hotel-inspired feel']
    },
    'pages/the-chevron-towel': {
      eyebrow: 'TIME FOR A BATHROOM REFRESH?',
      title: 'Beautifully considered, down to the last detail.',
      body: 'Distinctive Chevron texture and calming shades bring a little personality to the bathroom, with the softness you expect from Mallo.',
      points: ['Sculpted Chevron design', 'Lovely, tactile texture', 'Made for everyday use']
    },
    'collections/all-1': {
      eyebrow: 'THE MALLO UK EDIT',
      title: 'Find your kind of soft.',
      body: 'Love an airy, cloud-soft feel? Start with Signature Plush. Prefer traditional cotton? Meet Pure Cotton. Or explore Chevron and our limited-edition colours.',
      points: ['Signature Plush', 'Pure Cotton', 'Chevron & limited editions']
    },
    'collections/all': {
      eyebrow: 'THE MALLO UK EDIT',
      title: 'Find your kind of soft.',
      body: 'Beautifully textured towels, thoughtful colours and a little luxury in the everyday.'
    },
    'pages/about-us': {
      eyebrow: 'WELCOME TO MALLO',
      title: 'Everyday comfort, made a little more special.',
      body: 'We started with a simple belief: everyday towels should look beautiful, feel wonderful and be a pleasure to use. We are delighted to share the Mallo world with shoppers in the UK.'
    },
    'pages/faqs': {
      eyebrow: 'SHOPPING FROM THE UK?',
      title: 'A few useful things to know.',
      body: 'Check your delivery address at checkout for available options and charges. We have collected some common UK shopping questions below.'
    },
    'pages/contact': {
      eyebrow: 'QUESTIONS FROM THE UK?',
      title: 'We’re always happy to help.',
      body: 'Ask us about sizing, materials, delivery or an existing order. For delivery enquiries, please include your postcode.',
      cta: 'Email Mallo Support', externalUrl: 'mailto:support@mallotowels.com'
    },
    'pages/partners': {
      eyebrow: 'UK PARTNERSHIPS',
      title: 'Thoughtful gifts. Memorable little moments.',
      body: 'Planning employee gifts, a boutique hotel refresh, a spa experience, a wedding or a retail collaboration in the UK? Tell us about your project and quantities.',
      points: ['Corporate gifting', 'Hotels, spas & hospitality', 'Retail & special events']
    },
    'collections/limited-edition-releases': {
      eyebrow: 'SPECIAL-RELEASE COLOURS',
      title: 'Your favourite softness, in a new shade.',
      body: 'Our Marshmallow texture in limited-run colours designed to give your bathroom a little something extra. Check availability while your favourite is here.'
    },
    'collections/bath-essentials': {
      eyebrow: 'LITTLE COMFORTS',
      title: 'The finishing touches for a softer routine.',
      body: 'Explore little extras designed to make an ordinary bathroom moment feel rather lovely. Available items and delivery options are shown at checkout.'
    },
    'blogs/mallo-living': {
      eyebrow: 'THE MALLO LIVING JOURNAL',
      title: 'Good towel care. Even better everyday moments.',
      body: 'From keeping towels feeling fresh to styling a cosy bathroom, explore simple ideas for living a little softer.'
    },
    'policies/shipping-policy': {
      eyebrow: 'DELIVERY TO THE UK',
      title: 'Please check delivery costs and options at checkout.',
      body: 'Services, delivery times and charges depend on your order and full UK address. VAT, customs and courier fees may depend on where your order is sent from and how it is shipped. Contact us if you need clarification before ordering.',
      cta: 'Ask about UK delivery', path: 'pages/contact', simple: true
    },
    'policies/refund-policy': {
      eyebrow: 'RETURNS FROM THE UK',
      title: 'Please read the policy and contact us before returning items.',
      body: 'The published Returns Policy below explains our normal returns process. If you have a faulty, incorrect or unwanted item, contact us for instructions. Nothing in this notice changes your applicable statutory consumer rights.',
      cta: 'Contact our team', path: 'pages/contact', simple: true
    },
    cart: {
      eyebrow: 'CHECKING OUT FROM THE UK?',
      title: 'Confirm your delivery information before payment.',
      body: 'Your currency display is handled automatically by the shop. Check delivery charges, any applicable import costs and the final payment details at checkout.',
      simple: true
    },
    'pages/the-marshmallow-towel-a-gift-for-her': {
      eyebrow: 'A THOUGHTFUL LITTLE PRESENT',
      title: 'Give someone their new favourite towel.',
      body: 'From birthdays to thoughtful just-because gifts, our cloud-soft Marshmallow towels make everyday moments feel special.'
    },
    'pages/navy-blue-and-snow-white': {
      eyebrow: 'A BEAUTIFULLY SIMPLE PAIRING',
      title: 'Two timeless shades. The softness you love.',
      body: 'Meet Navy Blue and Snow White — a fresh, understated way to bring signature Marshmallow softness into your bathroom.'
    }
  };
  const UK_COLLECTION_FALLBACK = {
    eyebrow: 'MALLO UK',
    title: 'A little softer, every day.',
    body: 'Explore lovely textures, beautiful colours and towels designed for your everyday routine.'
  };
  const UK_FAQS = [
    ['Can I order Mallo towels to the UK?', 'Select the United Kingdom as your destination and enter your address at checkout to review available delivery services. Contact our team if you need help.'],
    ['Do you deliver to England, Scotland, Wales and Northern Ireland?', 'Available services depend on your full address and postcode. Enter your details at checkout to see what is offered for your location.'],
    ['How much is UK delivery?', 'Delivery prices are calculated for your address and order. Please rely on the final checkout information rather than any US-dollar promotional threshold.'],
    ['How long will my delivery take?', 'Timing depends on dispatch arrangements, the delivery service and your postcode. If you need an estimate before ordering, please contact support.'],
    ['Will I pay UK VAT or customs charges?', 'VAT, import duties or courier clearance charges may apply depending on the order value, where your parcel is sent from and how charges are collected. If this is unclear at checkout, please contact us before ordering.'],
    ['Can I pay in pounds?', 'The website displays currency automatically based on its market settings. The final charge currency and available methods should be checked at checkout.'],
    ['What is the difference between Signature Plush and Pure Cotton?', 'Signature Plush uses an 85% polyester / 15% nylon microfibre blend for a lightweight, cloud-soft feel. Pure Cotton uses 100% long-staple cotton at 430 GSM for a richer, more traditional cotton feel.'],
    ['Can I return an order from the UK?', 'Please read the published Returns Policy and contact our team before sending a return. Your applicable statutory consumer rights remain unaffected.']
  ];

  function ukEditorialText(input, region) {
    const trimmed = normalized(input);
    if (!trimmed) return input;
    if (region === 'chrome') {
      let out = input;
      UK_CHROME_PAIRS.forEach(([from, to]) => { if (out.includes(from)) out = out.split(from).join(to); });
      return out;
    }
    const pairs = UK_MAIN_COPY[pageKey] || [];
    const match = pairs.find(([from]) => normalized(from) === trimmed);
    if (match) return match[1];
    // Only neutralise the known sitewide US threshold; don't touch amounts.
    let out = input;
    UK_MAIN_NEUTRAL_SHIPPING.forEach(([from, to]) => { if (out.includes(from)) out = out.split(from).join(to); });
    return out;
  }
  function translateUKTree(root, region) {
    if (!root) return;
    if (region === 'main' && (pageKey.startsWith('policies/') || pageKey === 'cart')) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    for (const textNode of nodes) {
      if (shouldSkip(textNode.parentElement)) continue;
      if (textNode.parentElement.closest && textNode.parentElement.closest(
        '[data-mallo-uk],.jdgm-rev,.loox-review,.okeReviews,.spr-review,blockquote,' +
        '[data-review-id],.product-price,.cart-totals,[data-money],.product-form__buttons')) continue;
      const prev = UK_ORIGINALS.get(textNode);
      const original = prev && prev.rendered === textNode.nodeValue ? prev.original : textNode.nodeValue;
      const result = ukEditorialText(original, region);
      UK_ORIGINALS.set(textNode, { original, rendered: result });
      if (result !== textNode.nodeValue) textNode.nodeValue = result;
    }
  }
  function injectUKStyles() {
    if (document.getElementById('mallo-uk-v6-css')) return;
    const style = document.createElement('style');
    style.id = 'mallo-uk-v6-css';
    style.textContent = `
      [data-mallo-uk] {box-sizing:border-box;}
      .mallo-uk-panel {box-sizing:border-box;width:100%;background:linear-gradient(116deg,#fffafa,#fbeceb 53%,#f4effa);color:#203151;padding:clamp(24px,4vw,52px) 20px;}
      .mallo-uk-inner {max-width:1160px;margin:auto;display:flex;justify-content:space-between;align-items:center;gap:26px;min-width:0;}
      .mallo-uk-copy {max-width:800px;min-width:0;}
      .mallo-uk-eyebrow {display:block;color:#72629d;font-size:11px;font-weight:700;letter-spacing:.09em;margin-bottom:12px;}
      .mallo-uk-heading {font-size:clamp(24px,2.65vw,38px);line-height:1.24;font-weight:650;letter-spacing:-.02em;margin:0 0 12px;color:#203151;overflow-wrap:break-word;}
      .mallo-uk-body {font-size:clamp(14px,1.1vw,16px);line-height:1.75;color:#3e4e6e;margin:0;}
      .mallo-uk-benefits {display:flex;flex-wrap:wrap;gap:8px;padding:0;margin:18px 0 0;list-style:none;}
      .mallo-uk-benefits li {font-size:12px;font-weight:600;line-height:1.5;padding:7px 13px;border:1px solid #e0d7eb;background:#ffffffb8;border-radius:30px;}
      .mallo-uk-button {display:inline-flex;align-items:center;justify-content:center;text-align:center;min-height:44px;padding:12px 20px;border-radius:80px;background:#203151;color:#fff!important;text-decoration:none!important;font-size:13px;font-weight:700;flex-shrink:0;}
      .mallo-uk-panel--simple {padding:21px 20px;background:#faf8fd;border-block:1px solid #e9e3f1;}
      .mallo-uk-panel--simple .mallo-uk-heading {font-size:clamp(20px,2vw,27px);}
      .mallo-uk-faq {max-width:980px;margin:32px auto;padding:0 20px;color:#203151;}
      .mallo-uk-faq h2 {font-size:clamp(21px,2.3vw,30px);color:#203151;margin:0 0 16px;}
      .mallo-uk-faq details {border-bottom:1px solid #e8e5ee;padding:15px 0;}
      .mallo-uk-faq summary {font-size:15px;font-weight:650;cursor:pointer;color:#203151;}
      .mallo-uk-faq p {font-size:14px;line-height:1.7;color:#3e4e6e;margin:10px 0 0;}
      .mallo-uk-product {box-sizing:border-box;background:#fbf7fb;color:#203151;border:1px solid #e8dff0;border-radius:14px;margin:18px 0;padding:16px 18px;max-width:720px;min-width:0;}
      .mallo-uk-product strong {display:block;font-size:13px;margin-bottom:8px;}
      .mallo-uk-product ul {padding:0;margin:0;list-style:none;display:flex;flex-wrap:wrap;gap:8px 20px;}
      .mallo-uk-product li {font-size:12px;line-height:1.5;}
      .mallo-uk-product li::before {content:'✓';margin-right:7px;color:#72629d;font-weight:700;}
      @media (max-width:749px) {.mallo-uk-panel {padding:27px 18px;}.mallo-uk-panel--simple {padding:19px 16px;}.mallo-uk-inner {display:block;}.mallo-uk-button {margin-top:18px;white-space:normal;}.mallo-uk-faq {padding:0 18px;}}
    `;
    (document.head || document.documentElement).appendChild(style);
  }
  function populateUKPanel(panel, info) {
    const inner = el('div', 'mallo-uk-inner');
    const copy = el('div', 'mallo-uk-copy');
    if (info.eyebrow) copy.appendChild(el('span', 'mallo-uk-eyebrow', info.eyebrow));
    if (info.title) copy.appendChild(el('h2', 'mallo-uk-heading', info.title));
    if (info.body) copy.appendChild(el('p', 'mallo-uk-body', info.body));
    if (Array.isArray(info.points) && info.points.length) {
      const list = el('ul', 'mallo-uk-benefits');
      info.points.forEach(point => list.appendChild(el('li', '', point)));
      copy.appendChild(list);
    }
    inner.appendChild(copy);
    if (info.cta) {
      const a = el('a', 'mallo-uk-button', info.cta);
      a.href = info.externalUrl || href(info.path);
      inner.appendChild(a);
    }
    panel.appendChild(inner);
  }
  function mountUKPanel(main) {
    const info = UK_PANELS[pageKey] || (pageKey.startsWith('collections/') ? UK_COLLECTION_FALLBACK : null);
    if (!main || !info || main.querySelector('[data-mallo-uk="page-panel"]')) return;
    const isSimple = !!info.simple || pageKey.startsWith('policies/') || pageKey === 'cart';
    const heading = main.querySelector('h1');
    const section = heading && heading.closest('.shopify-section');
    const host = section && section.parentElement === main ? section : main.firstElementChild;
    if (!host) return;
    const panel = el('section', 'mallo-uk-panel' + (isSimple ? ' mallo-uk-panel--simple' : ''));
    panel.setAttribute('data-mallo-uk', 'page-panel');
    panel.setAttribute('lang', 'en-GB');
    panel.setAttribute('aria-label', 'Mallo UK collection information');
    populateUKPanel(panel, info);
    if (isSimple) host.before(panel); else host.after(panel);
  }
  function mountUKFAQs(main) {
    if (!main || pageKey !== 'pages/faqs' || main.querySelector('[data-mallo-uk="faq-questions"]')) return;
    const section = el('section', 'mallo-uk-faq');
    section.setAttribute('data-mallo-uk', 'faq-questions');
    section.setAttribute('lang', 'en-GB');
    section.appendChild(el('h2', '', 'Ordering from the UK — FAQs'));
    UK_FAQS.forEach(([question, answer]) => {
      const item = el('details');
      item.appendChild(el('summary', '', question));
      item.appendChild(el('p', '', answer));
      section.appendChild(item);
    });
    const a = el('a', 'mallo-uk-button', 'Contact Mallo Support');
    a.href = href('pages/contact');
    section.appendChild(a);
    main.appendChild(section);
  }
  function mountUKProductBenefits(main) {
    if (!main || !pageKey.startsWith('products/') || main.querySelector('[data-mallo-uk="product-benefits"]')) return;
    const heading = main.querySelector('h1');
    if (!heading) return;
    const productType = normalized(heading.textContent + ' ' + pageKey).toLowerCase();
    if (!/marshmallow|towel|chevron/.test(productType) || /soap|bath bomb|pumpkin/.test(productType)) return;
    const isCotton = /pure cotton|long.staple cotton/.test(productType);
    const isChevron = /chevron/.test(productType);
    const points = isCotton ? ['100% long-staple cotton', '430 GSM, plush feel', 'Beautifully textured comfort']
      : isChevron ? ['Sculpted Chevron design', 'Lovely, soft texture', 'Everyday comfort']
        : ['Velvety Marshmallow ridges', 'Lightweight, quick-drying design', 'Easy to wash and care for'];
    const card = el('aside', 'mallo-uk-product');
    card.setAttribute('data-mallo-uk', 'product-benefits');
    card.setAttribute('lang', 'en-GB');
    card.appendChild(el('strong', '', 'A little luxury for your everyday routine'));
    const list = el('ul');
    points.forEach(point => list.appendChild(el('li', '', point)));
    card.appendChild(list);
    const info = heading.closest('.product-information__content,.product-details,.product__info-wrapper,.product-info,.product-information');
    if (info && !info.closest('form')) info.appendChild(card);
    else {
      const section = heading.closest('.shopify-section');
      if (section && section.parentElement === main) section.after(card);
      else main.appendChild(card);
    }
  }
  function localiseUK() {
    injectUKStyles();
    const main = mainElement();
    if (main) {
      translateUKTree(main, 'main');
      mountUKProductBenefits(main);
      mountUKPanel(main);
      mountUKFAQs(main);
    }
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    [header, footer].forEach(item => translateUKTree(item, 'chrome'));
    for (const item of document.querySelectorAll('[class*="announcement-bar"],[class*="announcement_bar"],[id*="announcement-bar"]')) {
      if (!header || !header.contains(item)) translateUKTree(item, 'chrome');
    }
  }

  /* ========================= RENDERING ENGINE ======================= */
  function shouldSkip(element) {
    return !element || (element.closest && !!element.closest(EXCLUDED));
  }

  function translateRoot(root, pairs) {
    if (!root || !pairs.length) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach((textNode) => {
      if (shouldSkip(textNode.parentElement)) return;
      let updated = textNode.nodeValue;
      for (const pair of pairs) {
        const before = pair[0];
        if (!before || !updated.includes(before)) continue;
        updated = updated.split(before).join(pair[1]);
      }
      if (updated !== textNode.nodeValue) textNode.nodeValue = updated;
    });
  }

  function localiseAnnouncement() {
    if (!SETTINGS.philippines.banner) return;
    // Change only the text of the existing announcement, never its links,
    // countdown timer, checkout discount rules, or dynamic price fields.
    // The global USD shipping threshold may not apply to the Philippines.
    const promo = SETTINGS.philippines.promotion;
    const replacements = [
      ['AUTUMN IS HERE 🍂', promo.enabled ? '🇵🇭 PHILIPPINES LAUNCH • 25% OFF' : '🇵🇭 HELLO, PHILIPPINES!']
    ];
    if (promo.enabled) {
      replacements.push(['Enjoy 25% OFF with code AUTUMN25', 'Philippines launch: 25% OFF with code ' + promo.code]);
    } else {
      replacements.push(['Enjoy 25% OFF with code AUTUMN25', 'Discover your new favourite towels']);
    }
    if (SETTINGS.philippines.neutraliseFreeShippingClaim) {
      replacements.push(['Free tracked shipping over $99', 'Shipping options shown at checkout']);
      replacements.push(['Free shipping on orders over $99.', 'Shipping options are displayed at checkout.']);
    }
    for (const el of [document.querySelector('header'), document.querySelector('.shopify-section-group-header-group'), document.querySelector('footer')]) {
      if (el) translateRoot(el, replacements);
    }
    // Some Horizon announcement bars live outside the header element.
    for (const el of document.querySelectorAll('[class*="announcement-bar"], [class*="announcement_bar"], [id*="announcement-bar"]')) {
      translateRoot(el, replacements);
    }
  }

  function el(tag, className, textContent) {
    const result = document.createElement(tag);
    if (className) result.className = className;
    if (typeof textContent === 'string') result.textContent = textContent;
    return result;
  }

  function injectCSS() {
    if (document.getElementById('mallo-ph-styles')) return;
    const style = document.createElement('style');
    style.id = 'mallo-ph-styles';
    style.textContent = `
      [data-mallo-ph] { box-sizing:border-box; font-family:inherit; }
      .mallo-ph-panel { width:100%; background:linear-gradient(120deg,#fff7f8 0%,#fbeceb 56%,#f4effa 100%); color:#203151; padding:clamp(26px,4vw,58px) 20px; }
      .mallo-ph-inner { max-width:1160px; margin:0 auto; display:flex; gap:32px; align-items:center; justify-content:space-between; }
      .mallo-ph-copy { max-width:790px; }
      .mallo-ph-eyebrow { display:block; color:#72629d; letter-spacing:.12em; text-transform:uppercase; font-size:11px; font-weight:700; margin-bottom:12px; }
      .mallo-ph-heading { color:#203151; font-weight:650; line-height:1.15; letter-spacing:-.025em; font-size:clamp(23px,2.7vw,37px); margin:0 0 12px; }
      .mallo-ph-body { color:#3e4e6e; font-size:clamp(14px,1.15vw,16px); line-height:1.75; margin:0; }
      .mallo-ph-benefits { display:flex; flex-wrap:wrap; gap:8px; margin:18px 0 0; padding:0; list-style:none; }
      .mallo-ph-benefits li { border:1px solid #dcd4eb; border-radius:30px; padding:7px 12px; font-weight:600; font-size:12px; color:#203151; background:rgba(255,255,255,.68); }
      .mallo-ph-button { display:inline-flex; justify-content:center; text-align:center; align-items:center; min-height:44px; white-space:nowrap; background:#203151; color:white!important; border-radius:100px; text-decoration:none!important; font-size:13px; font-weight:700; padding:12px 19px; transition:opacity .2s; }
      .mallo-ph-button:hover { opacity:.85; }
      .mallo-ph-panel--simple { background:#faf8fd; padding:23px 20px; border-block:1px solid #e9e3f1; }
      .mallo-ph-panel--simple .mallo-ph-heading { font-size:clamp(19px,2vw,27px); }
      .mallo-ph-panel--simple .mallo-ph-body { font-size:14px; }
      .mallo-ph-questions { max-width:980px; margin:34px auto; padding:0 20px; color:#203151; }
      .mallo-ph-questions h2 { font-size:clamp(21px,2.5vw,30px); margin:0 0 18px; }
      .mallo-ph-questions details { padding:16px 0; border-bottom:1px solid #e6e6ef; }
      .mallo-ph-questions summary { font-weight:650; cursor:pointer; font-size:15px; }
      .mallo-ph-questions p { line-height:1.7; font-size:14px; color:#3e4e6e; margin:12px 0 0; }
      .mallo-ph-questions a { color:#203151; text-decoration:underline; }
      .mallo-ph-product { background:#fbf7fb; border:1px solid #e8dff0; border-radius:14px; margin:18px 0; padding:16px 18px; max-width:720px; color:#203151; }
      .mallo-ph-product strong { display:block; font-size:13px; margin-bottom:7px; }
      .mallo-ph-product ul { padding:0; margin:0; display:flex; flex-wrap:wrap; gap:8px 20px; list-style:none; }
      .mallo-ph-product li { font-size:12px; line-height:1.5; }
      .mallo-ph-product li::before { content:'✓'; margin-right:7px; color:#72629d; font-weight:700; }
      @media (max-width:749px) {
        .mallo-ph-inner { display:block; }
        .mallo-ph-copy { max-width:none; }
        .mallo-ph-button { margin-top:17px; white-space:normal; }
        .mallo-ph-panel { padding:28px 18px; }
        .mallo-ph-panel--simple { padding:19px 16px; }
        .mallo-ph-questions { padding:0 18px; }
        .mallo-ph-product { margin:12px 0; }
      }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  function mountPanel(host, info, key, before) {
    if (!host || !info || document.querySelector('[data-mallo-ph="' + key + '"]')) return;
    const section = el('section', 'mallo-ph-panel' + (info.simple ? ' mallo-ph-panel--simple' : ''));
    section.setAttribute('data-mallo-ph', key);
    section.setAttribute('aria-label', 'Mallo Philippines information');
    const wrap = el('div', 'mallo-ph-inner');
    const copy = el('div', 'mallo-ph-copy');
    if (info.eyebrow) copy.appendChild(el('span', 'mallo-ph-eyebrow', info.eyebrow));
    if (info.title) copy.appendChild(el('h2', 'mallo-ph-heading', info.title));
    if (info.body) copy.appendChild(el('p', 'mallo-ph-body', info.body));
    if (Array.isArray(info.points) && info.points.length) {
      const ul = el('ul', 'mallo-ph-benefits');
      info.points.forEach(item => ul.appendChild(el('li', '', item)));
      copy.appendChild(ul);
    }
    wrap.appendChild(copy);
    if (info.cta) {
      const a = el('a', 'mallo-ph-button', info.cta);
      a.href = info.externalUrl || href(info.url);
      wrap.appendChild(a);
    }
    section.appendChild(wrap);
    if (before) host.before(section);
    else host.after(section);
    log('Inserted panel:', key);
  }

  function mountPagePanel(main) {
    if (!SETTINGS.philippines.pagePanels || !main) return;
    const info = PH_PANELS[pageKey];
    if (!info) return;
    const isPolicyOrCart = pageKey.startsWith('policies/') || pageKey === 'cart';
    const h1 = main.querySelector('h1');
    const anchored = h1 && h1.closest('.shopify-section');
    // Hero-led landing pages: insert after hero. Plain policies: insert
    // before policy body (never change legal clauses themselves).
    const host = anchored && main.contains(anchored) && anchored.parentElement === main
      ? anchored : main.firstElementChild;
    if (!host) return;
    mountPanel(host, info, 'panel-' + pageKey.replace(/[^a-z0-9]/gi, '-'), isPolicyOrCart);
  }

  const PH_FAQ = [
    ['Can I order Mallo Towels from the Philippines?',
      'Select the Philippines as your destination and enter your shipping address at checkout to see whether delivery is available for your address. If you cannot complete checkout, please contact us.'],
    ['How much is shipping to Metro Manila or other provinces?',
      'Available shipping options and their charges are displayed during checkout after you enter your complete Philippine delivery address. Please rely on the final checkout amount rather than a general site-wide shipping promotion.'],
    ['How long will delivery take?',
      'Delivery estimates depend on your delivery address, carrier and dispatch arrangements. Check the information shown at checkout or contact our team for a more specific estimate.'],
    ['Can I pay with GCash, Maya or a Philippine bank card?',
      'The payment methods available to your order are displayed at checkout. We cannot promise that any particular wallet or payment method is supported unless it appears there.'],
    ['Will there be customs charges?',
      'International deliveries may involve customs duties, taxes or clearance charges depending on the order and delivery arrangement. Please contact our team before checkout if you need confirmation of who pays these charges.'],
    ['What is the difference between Signature Plush and Pure Cotton?',
      'Signature Plush uses our lightweight 85% polyester / 15% nylon microfibre blend, designed for cloud-soft comfort and easy everyday care. Pure Cotton uses 100% long-staple cotton at 430 GSM for a denser, hotel-inspired feel.'],
    ['Can I return my order from the Philippines?',
      'Returns are subject to the published Returns Policy, including its eligibility rules and sale-item exclusions. Please contact us for instructions before shipping a return internationally.']
  ];

  function addFAQs(main) {
    if (!SETTINGS.philippines.faqAdditions || pageKey !== 'pages/faqs' || !main) return;
    if (main.querySelector('[data-mallo-ph="faq-questions"]')) return;
    const container = el('section', 'mallo-ph-questions');
    container.setAttribute('data-mallo-ph', 'faq-questions');
    container.appendChild(el('h2', '', 'Philippines Shopping FAQs'));
    PH_FAQ.forEach(([q, a]) => {
      const item = el('details');
      item.appendChild(el('summary', '', q));
      item.appendChild(el('p', '', a));
      container.appendChild(item);
    });
    const p = el('p');
    p.appendChild(document.createTextNode('Still have a question? '));
    const a = el('a', '', 'Contact our team');
    a.href = href('pages/contact');
    p.appendChild(a);
    container.appendChild(p);
    main.appendChild(container);
  }

  function addProductBenefits(main) {
    if (!SETTINGS.philippines.productBenefits || !pageKey.startsWith('products/') || !main) return;
    if (main.querySelector('[data-mallo-ph="product-benefits"]')) return;
    const heading = main.querySelector('h1');
    if (!heading) return;
    const title = normalized(heading.textContent).toLowerCase();
    if (!/towel|marshmallow/i.test(title) || /soap|pumpkin|bath bomb/.test(title)) return;
    const isSignature = /2\.0|limited edition|signature plush/.test(title);
    const isChevron = /chevron/.test(title);
    const benefits = isSignature
      ? ['Signature cloud-soft ridges', 'Lightweight feel & quick-drying design', 'Easy everyday care']
      : isChevron
        ? ['Sculpted chevron texture', 'Soft & absorbent', 'Made for everyday use']
        : ['Beautifully textured softness', 'Everyday bathroom comfort', 'Easy care'];
    const card = el('aside', 'mallo-ph-product');
    card.setAttribute('data-mallo-ph', 'product-benefits');
    card.appendChild(el('strong', '', 'A little extra softness for your everyday routine'));
    const ul = el('ul');
    benefits.forEach(item => ul.appendChild(el('li', '', item)));
    card.appendChild(ul);
    // Place next to product info only when a safe, direct parent exists.
    // Do NOT insert inside product forms, price wrappers or variant selectors.
    const infoHost = heading.closest('.product-information__content, .product-details, .product__info-wrapper, .product-info, .product-information');
    if (infoHost && infoHost.parentElement && !infoHost.closest('form')) {
      infoHost.appendChild(card);
    } else {
      const section = heading.closest('.shopify-section');
      if (section && section.parentElement === main) section.after(card);
      else main.appendChild(card);
    }
  }

  function handlePromoText(root) {
    if (!SETTINGS.philippines.promotion.enabled || !root) return;
    const offer = SETTINGS.philippines.promotion;
    // Intentionally no price/discount mutation. Only editorial panel copy.
    const intro = root.querySelector('[data-mallo-ph="panel-home"] .mallo-ph-eyebrow');
    if (intro) intro.textContent = offer.headline;
    if (offer.code && intro) intro.setAttribute('title', 'Use code ' + offer.code + ' if eligible');
  }

  function update() {
    if (!document.body) return;
    const main = mainElement();
    if (JP) {
      localiseJapan();
    } else if (MX) {
      localiseMexico();
    } else if (UK) {
      localiseUK();
    } else if (PH) {
      injectCSS();
      localiseAnnouncement();
      if (main) {
        translateRoot(main, COPY[pageKey] || []);
        // Some landing pages also include an embedded US-dollar-specific
        // free-shipping claim outside the main announcement bar.
        if (SETTINGS.philippines.neutraliseFreeShippingClaim) {
          translateRoot(main, [
            ['Free shipping on orders over $99.', 'Shipping options are displayed at checkout.'],
            ['Free tracked shipping over $99', 'Shipping options shown at checkout']
          ]);
        }
        mountPagePanel(main);
        addFAQs(main);
        addProductBenefits(main);
        handlePromoText(main);
      }
    } else if ((country === 'AU' || country === 'CA') && pageKey === 'home' && main) {
      translateRoot(main, AU_CA_HOME);
    }
  }

  // Shopify sections can re-render. Observe new nodes and text updates,
  // debounce, and make every operation idempotent. Avoid changing dynamic
  // price/variant/checkout elements altogether.
  let queued = false;
  let stopped = false;
  const observer = new MutationObserver((mutations) => {
    if (stopped || queued) return;
    // Ignore changes produced solely inside our custom panels.
    const relevant = mutations.some(m => {
      const target = m.target.nodeType === Node.TEXT_NODE ? m.target.parentElement : m.target;
      return !target || !target.closest || !target.closest('[data-mallo-ph], [data-mallo-jp], [data-mallo-mx], [data-mallo-uk]');
    });
    if (!relevant) return;
    queued = true;
    setTimeout(() => { queued = false; if (!stopped) update(); }, 90);
  });

  function start() {
    if (!/^[A-Z]{2}$/.test(country)) {
      console.warn('[Mallo localisation] No Shopify country detected. Use the Liquid context in the install guide.');
      return;
    }
    if (!PH && !JP && !MX && !UK && !(country === 'AU' || country === 'CA')) return;
    if (JP) japanLanguage = initializeJapanLanguage();
    if (MX) mexicoLanguage = getMexicoLanguage();
    update();
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });
    window.MalloLocalization = {
      version: '6.0-PH-JP-MX-GB', country, language, pageKey, preview,
      get japanLanguage() { return JP ? japanLanguage : null; },
      setJapanLanguage,
      get mexicoLanguage() { return MX ? mexicoLanguage : null; },
      setMexicoLanguage,
      refresh: update,
      stop: () => { stopped = true; observer.disconnect(); }
    };
    log('Enabled', { country, pageKey, preview, rootPath, japanLanguage: JP ? japanLanguage : null, mexicoLanguage: MX ? mexicoLanguage : null });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
